import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageCircle, RotateCcw, Loader2, User, Bot, Download, FileText, Maximize, Minimize, X, Pin, ChevronDown, ChevronUp, Square, Copy, Check, Edit, RefreshCw, Trash2, Key, Eye, EyeOff, Image } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { openRouterService, ChatMessage, BYOKService, BrowserIdService } from '../services/openRouterService';
import { useAuth } from '../services/AuthContext';
import { useCommentsActions } from './Comments';
import { sessionService, SessionContextItem, ChatConversation, SessionService } from '../services/sessionService';
import SessionContextManager from './SessionContextManager';
import ConversationSidebar from './ConversationSidebar';
import { BatchGenerateModal } from './BatchGenerateModal';
import ErrorBoundary from './ErrorBoundary';
import { validateAndLogXML } from '../utils/xmlSanitizer';
import TimeCodeWidget from './TimeCodeWidget';
import AudioClipCard from './AudioClipCard';
import TimelineViewerModal from './TimelineViewerModal';
import { parseTimeCode, timeCodeToSeconds as timeToSeconds } from '../utils/timeCodeParser';
import { useAudio } from '../contexts/AudioContext';
import { useAudioUI } from '../contexts/AudioUIContext';
import { useShareLink } from '../contexts/ShareLinkContext';

// Comprehensive storytelling context for video editing enhancement
const STORYTELLING_CONTEXT = `
## Comprehensive Storytelling Guide for Video Editing and Content Analysis

### Cold Opens and Hooks – Grabbing Attention from the First Second

**What it is:** A cold open is an opening scene that plunges the viewer directly into an interesting or dramatic moment before any intro or context. On YouTube, this is often the first 5-15 seconds of the video – and it's critical. A cold open serves as a hook: something so intriguing or unexpected that the viewer must keep watching. It could be a question, a bold statement, a mini-story, a surprising fact, or an emotional moment. The key is that it's presented upfront with no preamble – you drop the viewer in media res ("in the middle of things").

**Why it works:** Online, attention is a scarce commodity. Viewers decide within seconds whether to stay or click away. A strong hook promises value or intrigue, giving them a reason to watch on. A cold open dunks the audience right into the action or mystery, provoking immediate questions: "Whoa, what's happening here? How did we get here? I need to know more." It leverages curiosity and sets the tone and theme quickly.

### Emotional Stakes – Making Viewers Care

**What it is:** "Emotional stakes" means the human element that's at risk in your story – the feelings, relationships, or personal consequences riding on the outcome. It's basically answering the questions: "Why does this story matter, and to whom?" If your narrative is purely factual with no sense of personal impact, the stakes feel low. Emotional stakes introduce things like a protagonist's dream on the line, a community in peril, or a family legacy at risk.

**Why it works:** Simply put, if the audience doesn't care, nothing else matters. Emotional stakes are what make viewers care. They trigger empathy and investment. When we experience a story with relatable characters and emotions, our brains release oxytocin – the bonding hormone – which increases trust and empathy. High stakes, especially those involving urgency or risk, also trigger norepinephrine (our alertness chemical). This means viewers pay more attention and are more likely to remember what they see.

### Foreshadowing – Hinting at Future Twists

**What it is:** Foreshadowing is when you drop subtle clues about what's going to happen later in the story. In a script, this might be an early hint or symbolic detail that seems minor at first but pays off down the line. It sets up viewer expectations – sometimes fulfilling them, sometimes subverting them.

**Why it works:** Foreshadowing builds anticipation and dramatic tension. Psychologically, viewers love to predict outcomes. A hint gets the audience's mental gears turning as they try to figure out future events.

### Suspenseful Reveals – Building Tension then Unveiling

**What it is:** A suspenseful reveal means deliberately withholding a key piece of information and then revealing it at the perfect moment for maximum impact. It's the "big twist" or the answer to a burning question that you've teased throughout the narrative.

**Why it works:** Suspense is all about keeping the audience guessing. By raising questions and delaying the answers, you create mystery and engagement. Our brains actually reward us for this – suspense and unresolved situations keep the brain's dopamine-based reward system activated in anticipation.

### Tension and Release Cycles – Keeping the Audience on the Edge

**What it is:** Tension and release is the rhythmic cycle of building suspense or intensity (tension), then giving the audience a moment of resolution or calm (release). It's like tightening a spring and then letting it uncoil for a beat, only to tighten it again.

**Why it works:** If a story is all at one level (all tension or all calm), viewers eventually become desensitized or bored. Our brains start tuning out a constant stimulus. By alternating the intensity, you "wake up" the audience's attention repeatedly.

### Chapters and Escalation Pacing – Structuring for Progress

**What it is:** Think of your script like a book with chapters or a series of escalating segments. Chaptering means dividing your video into clear sections or phases – each with its own mini-story or focus – that together build the full narrative. Escalation pacing means that with each chapter or section, the stakes, tension, or complexity increases.

**Why it works:** Structured chapters help both you and the viewer. For you (the creator), it's easier to organize the script and ensure a logical flow. For the viewer, it makes the content scannable and digestible. Escalation is important because if every part stayed at the same level, interest could plateau.

### Contrast – Highlighting Differences to Drive Points Home

**What it is:** Contrast in storytelling means juxtaposing opposites or extremes to emphasize a point. This could be contrasting characters (hero vs. villain), outcomes (success vs. failure), scenarios (boom vs. bust), or even tones (serious vs. humorous).

**Why it works:** Our brains notice change and difference. If everything is on one note, it becomes background noise. The moment something changes, our attention snaps back. In narrative, contrast makes your message clearer.

### Relatable Analogies and Metaphors – Explaining Complex Ideas Simply

**What it is:** An analogy or metaphor compares a complex concept to something simple and familiar, making the concept easier to grasp. It's when you say "X is like Y" to leverage what the audience already understands.

**Why it works:** Analogies bridge new information to existing knowledge. They help viewers latch onto unfamiliar ideas by connecting them to frameworks they already know. Psychologically, this reduces mental friction – people learn and remember better when they can relate new ideas to everyday life.

### Callbacks and Circular Structure – Coming Full Circle

**What it is:** A callback is a reference to something earlier in your script – an earlier event, line, or theme – later on in the story. It creates a connective thread that runs through your narrative. A circular structure means your story ends where it began, creating a satisfying sense of closure.

**Why it works:** Callbacks give the audience that rewarding "I see what you did there!" moment. By tying back to an earlier moment, you create unity and coherence in the story. Circular storytelling in particular highlights how far the story or characters have come.

### Red Herrings and Controlled Misdirection – Leading and Surprising Your Audience

**What it is:** A red herring is a false clue – a narrative redirection that leads the audience to a wrong assumption. Misdirection in storytelling is the broader technique of purposefully guiding your viewers' attention away from the truth, only to reveal the real truth later.

**Why it works:** When used well, red herrings and misdirection create intrigue and surprise. They enhance suspense by encouraging viewers to theorize and guess, often leading them down a wrong path so that the real outcome is more impactful.

### Framing Stories (Embedded Narratives) – Stories Within Stories

**What it is:** A frame narrative (or framing story) is when your main story contains another story inside of it. Essentially, one narrative frames another. Often you have an outer narrator or context that introduces an inner narrative.

**Why it works:** Framing can make abstract or dry content more relatable by embedding it in a narrative context. It also adds a layer of intrigue – viewers realize there's a story being told and want to see how it ties back to the main narrative.

### Key Principles for Video Editing and Content Analysis

**For Identifying Story Structure:**
- Look for chapter breaks and escalation patterns
- Identify tension and release cycles
- Notice how emotional stakes are established and maintained
- Spot foreshadowing elements that pay off later
- Recognize circular structure and callbacks

**For Content Enhancement:**
- Identify opportunities for stronger hooks in the opening
- Look for places where analogies could clarify complex concepts
- Find moments where contrast could emphasize key points
- Spot potential for suspenseful reveals
- Recognize where framing stories could add engagement

**For Pacing Analysis:**
- Map out the tension and release rhythm
- Identify if chapters escalate properly
- Check if there are appropriate breathers between intense moments
- Ensure emotional stakes are maintained throughout
- Verify that reveals are properly set up and paid off

This comprehensive storytelling framework provides the foundation for analyzing and enhancing video content through narrative techniques that have proven effective across literature, film, and successful YouTube channels. Each technique can be applied individually or combined for maximum impact, always serving the ultimate goal of keeping the audience engaged and emotionally invested in the content.
`;

// YouTube retention data types
interface RetentionDataPoint {
  percent: number;
  ratio: number;
  absoluteRetention: number;
  relativeRetention: number;
  timestampSeconds?: number;
}

interface VideoStats {
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  subscribersGained: number | null;
  subscribersLost: number | null;
  estimatedMinutesWatched: number | null;
  averageViewDuration: number | null;
  averageViewPercentage: number | null;
  videoTitle: string | null;
  videoPublishedAt: string | null;
}

interface YouTubeRetentionData {
  sessionId: string;
  videoId: string;
  retentionData: RetentionDataPoint[];
  videoDurationSeconds: number | null;
  videoStats?: VideoStats;
  fetchedAt: string;
  fetchCount?: number;
  cached?: boolean;
}

interface ChatInterfaceProps {
  transcript: string;
  model: string;
  onModelChange: (model: string) => void;
  supportedModels: string[];
  currentTtmlFiles?: Array<{ name: string; content: string }>;
  sessionId?: string | null;
  transcriptionData?: any; // Deepgram transcription data for story visualization
  transcriptionChecksum?: string; // For audio playback URLs
  speakerLabels?: { [speakerId: number]: string }; // Speaker names mapping
  shareToken?: string;
  isPublicView?: boolean;
  narrativeData?: any; // Topic map/narrative structure data
  youtubeRetentionData?: YouTubeRetentionData | null; // YouTube retention & stats data
}

interface TTMLFile {
  id: string;
  content: string;
  messageIndex: number;
  createdAt: Date;
  filename?: string; // Optional filename for LLM-provided TTML files
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  transcript,
  model,
  onModelChange,
  supportedModels,
  currentTtmlFiles = [],
  sessionId,
  transcriptionData,
  transcriptionChecksum,
  speakerLabels = {},
  shareToken,
  isPublicView,
  narrativeData,
  youtubeRetentionData
}) => {
  const { token } = useAuth();
  const { permissions: sharePermissions } = useShareLink();
  const { addPinnedMessage } = useCommentsActions(sessionId ?? null, isPublicView ? (shareToken ?? null) : token);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const { loadAudio, playSegment, pause, isPlaying: globalIsPlaying } = useAudio();
  const { playingWidgetId, setPlayingWidgetId, setHoveredTimeRange } = useAudioUI();
  const brandLinkClass = 'text-[#B57700] dark:text-[#FFCF74] hover:text-[#8F5E00] dark:hover:text-[#FFE3A1] underline decoration-[#FFB52B]/55 underline-offset-2';
  const brandGoldChipClass = 'border border-[#FFB52B]/40 bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1] dark:hover:bg-[#FFB52B]/[0.24]';
  const brandMutedChipClass = 'border border-[#D6DCE4] bg-[#EEF1F3] text-[#566377] hover:bg-[#E1E7EE] dark:border-[#445166] dark:bg-[#273244] dark:text-[#CAD1DB] dark:hover:bg-[#2F3B4E]';
  const brandNavyChipClass = 'border border-[#445166] bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]';
  const brandPrimaryButtonClass = 'bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.9)] hover:bg-[#FFC54D]';
  const brandSecondaryButtonClass = 'bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]';
  const brandNoticePanelClass = 'border border-[#E7B14C]/45 bg-[#FFF8EA] text-[#6F5934] dark:border-[#79613A] dark:bg-[#2B3241] dark:text-[#E4D7BD]';
  const brandCheckboxClass = 'rounded border-[#C9BEAA] text-[#FFB52B] focus:ring-[#FFB52B] focus:ring-offset-0';
  
  // Debug log the currentTtmlFiles prop
  console.log('[CHAT] ChatInterface received currentTtmlFiles prop:', currentTtmlFiles);
  console.log('[CHAT] ChatInterface received youtubeRetentionData prop:', youtubeRetentionData ? { videoId: youtubeRetentionData.videoId, hasStats: !!youtubeRetentionData.videoStats } : null);
  
  // Create a truly memoized AudioClipCard that doesn't re-render on audio state changes
  const MemoizedAudioClipCard = useMemo(() => {
    return memo(({ clipData, onLoadSession, currentSessionId, widgetId }: any) => {
      const { playSegment, pause } = useAudio();
      const { playingWidgetId, setPlayingWidgetId, setHoveredTimeRange } = useAudioUI();
      
      const isPlaying = playingWidgetId === `widget_${widgetId}`;
      
      const handlePlay = useCallback((sessionId: string, startSeconds: number, endSeconds?: number) => {
        const newWidgetId = `widget_${sessionId}_${startSeconds}_${endSeconds || 'single'}`;
        setPlayingWidgetId(newWidgetId);
        playSegment(startSeconds, endSeconds);
      }, [playSegment, setPlayingWidgetId]);
      
      const handleStop = useCallback(() => {
        setPlayingWidgetId(null);
        pause();
      }, [pause, setPlayingWidgetId]);
      
      const handleHoverTimeRange = useCallback((start: number, end?: number) => {
        setHoveredTimeRange({ start, end });
      }, [setHoveredTimeRange]);
      
      const handleHoverEnd = useCallback(() => {
        setHoveredTimeRange(null);
      }, [setHoveredTimeRange]);
      
      return (
        <AudioClipCard
          clipData={clipData}
          onLoadSession={onLoadSession}
          currentSessionId={currentSessionId}
          isPlaying={isPlaying}
          isCurrentAudio={isPlaying}
          onPlay={handlePlay}
          onStop={handleStop}
          onHoverTimeRange={handleHoverTimeRange}
          onHoverEnd={handleHoverEnd}
        />
      );
    }, (prevProps, nextProps) => {
      // Only re-render if clip data or session changes
      return prevProps.clipData === nextProps.clipData &&
        prevProps.currentSessionId === nextProps.currentSessionId &&
        prevProps.widgetId === nextProps.widgetId;
    });
  }, []);

  // Load uploaded files and settings when sessionId changes (for session restoration)
  useEffect(() => {
    const loadSessionData = async () => {
      if (sessionId && token) {
        try {
          const sessionData = await sessionService.getSession(sessionId, token);
          
          // Load audio if available
          if (sessionData.audioUrl) {
            console.log('[ChatInterface] Loading audio from session:', sessionData.audioUrl);
            loadAudio(sessionData.audioUrl);
          }
          
          // Load uploaded files
          if (sessionData.uploadedFiles && sessionData.uploadedFiles.length > 0) {
            console.log(`[CHAT] Loading ${sessionData.uploadedFiles.length} files from session`);
            const filesWithContext = sessionData.uploadedFiles.map(file => ({
              ...file,
              uploadedAt: typeof file.uploadedAt === 'string' ? file.uploadedAt : new Date(file.uploadedAt).toISOString(),
              includedInContext: file.includedInContext !== undefined ? file.includedInContext : true // Default to enabled
            }));
            setUploadedFiles(filesWithContext);
            
            // Initialize toggle states
            const toggleStates: Record<string, boolean> = {};
            filesWithContext.forEach(file => {
              toggleStates[file.id] = file.includedInContext;
            });
            setFileContextToggles(toggleStates);
          }

          // Load session context if available
          let currentSessionContext: any[] = [];
          if (sessionData.sessionContext && sessionData.sessionContext.length > 0) {
            console.log(`[CHAT] Loading ${sessionData.sessionContext.length} sessions from context`);
            currentSessionContext = sessionData.sessionContext;
          }

          // Note: A session should not be added to its own context
          // The current session's transcript is always available without being in the context
          
          setSessionContext(currentSessionContext);

          // Load chat settings if available
          if (sessionData.chatSettings) {
            console.log(`[CHAT] Loading chat settings from session:`, sessionData.chatSettings);
            if (sessionData.chatSettings.selectedModel) {
              onModelChange(sessionData.chatSettings.selectedModel);
            }
            if (sessionData.chatSettings.useStorytellingContext !== undefined) {
              setUseStorytellingContext(sessionData.chatSettings.useStorytellingContext);
            }
            if (sessionData.chatSettings.enableWebSearch !== undefined) {
              setEnableWebSearch(sessionData.chatSettings.enableWebSearch);
            }
            if (sessionData.chatSettings.textOnlyTimeReferences !== undefined) {
              setTextOnlyTimeReferences(sessionData.chatSettings.textOnlyTimeReferences);
            }
            if (sessionData.chatSettings.includeTTMLInContext !== undefined) {
              setIncludeTTMLInContext(sessionData.chatSettings.includeTTMLInContext);
            }
            if (sessionData.chatSettings.includeTopicMapInContext !== undefined) {
              setIncludeTopicMapInContext(sessionData.chatSettings.includeTopicMapInContext);
            }
            if (sessionData.chatSettings.includeYouTubeStatsInContext !== undefined) {
              setIncludeYouTubeStatsInContext(sessionData.chatSettings.includeYouTubeStatsInContext);
            }
            if (sessionData.chatSettings.includeCommentsInContext !== undefined) {
              setIncludeCommentsInContext(sessionData.chatSettings.includeCommentsInContext);
            }
          }

        } catch (error) {
          console.error('Failed to load session data:', error);
        }
      } else if (!sessionId) {
        // Clear files and session context when no session
        setUploadedFiles([]);
        setFileContextToggles({});
        setSessionContext([]);
        // Reset chat settings to defaults when no session
        setUseStorytellingContext(false);
        setEnableWebSearch(false);
      }
    };

    loadSessionData();
  }, [sessionId, token, onModelChange]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string>('');
  const [ttmlFiles, setTtmlFiles] = useState<TTMLFile[]>([]);
  const [ttmlNotification, setTtmlNotification] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ttmlAddedToSystem, setTtmlAddedToSystem] = useState(false);
  const [useStorytellingContext, setUseStorytellingContext] = useState(false);
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [reasoningStrength, setReasoningStrength] = useState<string>('none'); // none, minimal, low, medium, high, xhigh
  const [textOnlyTimeReferences, setTextOnlyTimeReferences] = useState(false);

  // Context window tracking state
  const [contextUsage, setContextUsage] = useState<{
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    maxTokens: number;
    percentUsed: number;
    compactionCount: number;
    status: 'green' | 'yellow' | 'orange' | 'red';
  } | null>(null);

  // Timeline viewer and audio state
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [timelineModalData, setTimelineModalData] = useState<{
    sessionId: string;
    startSeconds: number;
    endSeconds?: number;
    sessionTitle?: string;
  } | null>(null);
  const [currentPlayingTimeCode, setCurrentPlayingTimeCode] = useState<string | null>(null);
  const playingSegmentEndRef = useRef<number | null>(null);
  
  // Conversation management state
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  // Default to collapsed on mobile (< 768px)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth < 768);
  const [isGeneratingConversationName, setIsGeneratingConversationName] = useState<string | null>(null);
  const [isBatchGenerateModalOpen, setIsBatchGenerateModalOpen] = useState(false);
  const [isBatchStarting, setIsBatchStarting] = useState(false);

  // Batch generation background progress
  const [activeBatch, setActiveBatch] = useState<{
    batchId: string;
    totalJobs: number;
    completed: number;
    failed: number;
  } | null>(null);

  // Active chat jobs per conversation (async processing) - keyed by conversationId
  const [activeJobs, setActiveJobs] = useState<Map<string, {
    jobId: string;
    prompt: string;
    status: 'pending' | 'running';
    startedAt?: string;
  }>>(new Map());

  // Self-healing retry mechanism state
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(3);
  const [lastFailedTool, setLastFailedTool] = useState<string | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const [isAutomatedRetryInProgress, setIsAutomatedRetryInProgress] = useState(false);
  
  // Helper to get user's saved chat defaults from localStorage
  const getUserChatDefaults = useCallback(() => {
    try {
      const saved = localStorage.getItem('mlst_chat_defaults');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('[CHAT] Failed to load chat defaults:', e);
    }
    return null;
  }, []);

  // Helper to save user's chat defaults to localStorage
  const saveUserChatDefaults = useCallback((settings: any) => {
    try {
      localStorage.setItem('mlst_chat_defaults', JSON.stringify(settings));
    } catch (e) {
      console.error('[CHAT] Failed to save chat defaults:', e);
    }
  }, []);

  // Helpers for storing public share conversations in localStorage (ephemeral, privacy-preserving)
  const getPublicShareConversationsKey = useCallback(() => {
    return `mlst_public_conversations_${sessionId}`;
  }, [sessionId]);

  const getPublicShareConversations = useCallback((): ChatConversation[] => {
    if (!sessionId) return [];
    try {
      const stored = localStorage.getItem(getPublicShareConversationsKey());
      if (stored) {
        const parsed = JSON.parse(stored);
        // Restore Date objects
        return parsed.map((conv: any) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.updatedAt),
          messages: conv.messages?.map((msg: any) => ({
            ...msg,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
          })) || []
        }));
      }
    } catch (e) {
      console.error('[CHAT] Failed to load public share conversations from localStorage:', e);
    }
    return [];
  }, [sessionId, getPublicShareConversationsKey]);

  const savePublicShareConversations = useCallback((convs: ChatConversation[]) => {
    if (!sessionId) return;
    try {
      localStorage.setItem(getPublicShareConversationsKey(), JSON.stringify(convs));
      console.log('[CHAT] Saved public share conversations to localStorage:', convs.length);
    } catch (e) {
      console.error('[CHAT] Failed to save public share conversations to localStorage:', e);
    }
  }, [sessionId, getPublicShareConversationsKey]);

  const getPublicShareActiveConversationId = useCallback((): string | null => {
    if (!sessionId) return null;
    try {
      return localStorage.getItem(`mlst_public_active_conv_${sessionId}`);
    } catch (e) {
      return null;
    }
  }, [sessionId]);

  const savePublicShareActiveConversationId = useCallback((convId: string | null) => {
    if (!sessionId) return;
    try {
      if (convId) {
        localStorage.setItem(`mlst_public_active_conv_${sessionId}`, convId);
      } else {
        localStorage.removeItem(`mlst_public_active_conv_${sessionId}`);
      }
    } catch (e) {
      console.error('[CHAT] Failed to save public share active conversation ID:', e);
    }
  }, [sessionId]);

  // Topic map/TTML/YouTube/Comments context inclusion state (initialized from localStorage)
  const defaults = getUserChatDefaults();
  const [includeTTMLInContext, setIncludeTTMLInContext] = useState(defaults?.includeTTMLInContext ?? true);
  const [includeTopicMapInContext, setIncludeTopicMapInContext] = useState(defaults?.includeTopicMapInContext ?? true);
  const [includeYouTubeStatsInContext, setIncludeYouTubeStatsInContext] = useState(defaults?.includeYouTubeStatsInContext ?? true);
  const [includeCommentsInContext, setIncludeCommentsInContext] = useState(defaults?.includeCommentsInContext ?? false);
  const [sessionComments, setSessionComments] = useState<any[]>([]);

  // BYOK (Bring Your Own Key) state for public shares
  const [userApiKey, setUserApiKey] = useState<string>('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string>('');

  // Initialize BYOK from localStorage on mount
  useEffect(() => {
    if (isPublicView) {
      const storedKey = BYOKService.getApiKey();
      if (storedKey) {
        setUserApiKey(storedKey);
      }
    }
  }, [isPublicView]);

  // Load comments when toggle is enabled
  useEffect(() => {
    const loadComments = async () => {
      if (includeCommentsInContext && sessionId && token) {
        try {
          const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:7777';
          const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setSessionComments(data.comments || []);
            console.log(`[CHAT] Loaded ${data.comments?.length || 0} comments for context`);
          }
        } catch (error) {
          console.error('[CHAT] Failed to load comments:', error);
        }
      } else if (!includeCommentsInContext) {
        setSessionComments([]);
      }
    };
    loadComments();
  }, [includeCommentsInContext, sessionId, token]);

  // Save chat defaults to localStorage whenever they change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveUserChatDefaults({
        includeTTMLInContext,
        includeTopicMapInContext,
        includeCommentsInContext,
        includeYouTubeStatsInContext,
        useStorytellingContext,
        enableWebSearch,
        textOnlyTimeReferences
      });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [includeTTMLInContext, includeTopicMapInContext, includeCommentsInContext, includeYouTubeStatsInContext, useStorytellingContext, enableWebSearch, textOnlyTimeReferences, saveUserChatDefaults]);

  // Poll for batch generation progress
  useEffect(() => {
    if (!activeBatch || !sessionId || !token) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await SessionService.getBatchStatus(sessionId, activeBatch.batchId, token);

        setActiveBatch(prev => prev ? {
          ...prev,
          completed: status.completed,
          failed: status.failed,
        } : null);

        if (status.isComplete) {
          // Reload conversations to get the completed results
          const session = await sessionService.getSession(sessionId, token);
          if (session?.conversations) {
            setConversations(session.conversations);
          }
          // Clear the batch indicator
          setActiveBatch(null);
        }
      } catch (error) {
        console.error('Failed to poll batch status:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [activeBatch, sessionId, token]);

  // Poll for active job status for all conversations with active jobs
  useEffect(() => {
    if (activeJobs.size === 0 || !sessionId || !token) return;

    const pollInterval = setInterval(async () => {
      // Poll each active job
      for (const [convId, activeJob] of activeJobs.entries()) {
        try {
          const status = await SessionService.getAsyncChatJobStatus(sessionId, activeJob.jobId, token);
          const job = status.job;

          // Update context usage from job status (for current conversation)
          if (convId === activeConversationId && job.context) {
            setContextUsage(job.context);
          }

          if (job.status === 'completed') {
            // Capture final context data before clearing
            if (convId === activeConversationId && job.context) {
              setContextUsage(job.context);
            }

            // If this is the current conversation, update messages directly
            if (convId === activeConversationId && job.result) {
              // FIRST: Clear streaming state to prevent duplicate message display
              setIsStreaming(false);
              setStreamingContent('');
              streamingContentRef.current = '';

              // Check if the message was already added to prevent duplicates
              const lastMessage = messages[messages.length - 1];
              const alreadyAdded = lastMessage && lastMessage.role === 'assistant' && lastMessage.content === job.result;

              if (!alreadyAdded) {
                // THEN: Add the final message
                const newMessages: ChatMessage[] = [...messages, { role: 'assistant', content: job.result }];
                setMessages(newMessages);

                // Save conversation state
                await saveCurrentConversationState(true, newMessages);

                // Auto-name if needed
                checkAndAutoNameConversation(newMessages);
              }
            }
            // Remove job from active jobs map
            setActiveJobs(prev => {
              const updated = new Map(prev);
              updated.delete(convId);
              return updated;
            });
          } else if (job.status === 'failed' || job.status === 'cancelled') {
            if (convId === activeConversationId) {
              if (job.status === 'failed') {
                setError(job.error || 'Chat request failed');
              }
              // Clear streaming content
              setStreamingContent('');
              streamingContentRef.current = '';
              setIsStreaming(false);
            }
            // Remove job from active jobs map
            setActiveJobs(prev => {
              const updated = new Map(prev);
              updated.delete(convId);
              return updated;
            });
          } else if (job.status === 'pending' || job.status === 'running') {
            // Update status if changed
            setActiveJobs(prev => {
              const updated = new Map(prev);
              const existing = updated.get(convId);
              if (existing) {
                updated.set(convId, {
                  ...existing,
                  status: job.status as 'pending' | 'running',
                  startedAt: job.startedAt,
                });
              }
              return updated;
            });

            // If this is the current conversation and there's streamed content, show it
            if (convId === activeConversationId && job.streamedContent) {
              setStreamingContent(job.streamedContent);
              streamingContentRef.current = job.streamedContent;
            }
          }
        } catch (error) {
          console.error('[ASYNC-CHAT] Failed to poll job status for conversation', convId, ':', error);
        }
      }
    }, 1000); // Poll every 1 second for smoother streaming

    return () => clearInterval(pollInterval);
  }, [activeJobs, sessionId, token, messages, activeConversationId]);

  // Check for active jobs when conversation changes (for resuming on page return)
  useEffect(() => {
    const checkActiveJobs = async () => {
      if (!sessionId || !activeConversationId || !token) return;

      // Skip if we already have an active job for this conversation
      if (activeJobs.has(activeConversationId)) return;

      try {
        const result = await SessionService.getActiveJobsForConversation(sessionId, activeConversationId, token);

        if (result.jobs && result.jobs.length > 0) {
          // Resume polling for the first active job
          const job = result.jobs[0];
          console.log('[ASYNC-CHAT] Found active job to resume:', job.jobId);

          // Add a pending user message for the active job's prompt
          const hasMatchingMessage = messages.some(m => m.role === 'user' && m.content === job.prompt);
          if (!hasMatchingMessage) {
            setMessages(prev => [...prev, { role: 'user', content: job.prompt }]);
          }

          // Fetch full job status to get any streamed content
          try {
            const fullStatus = await SessionService.getAsyncChatJobStatus(sessionId, job.jobId, token);
            if (fullStatus.job.streamedContent) {
              setStreamingContent(fullStatus.job.streamedContent);
              streamingContentRef.current = fullStatus.job.streamedContent;
            }
          } catch (e) {
            console.warn('[ASYNC-CHAT] Could not fetch streamed content:', e);
          }

          setActiveJobs(prev => {
            const updated = new Map(prev);
            updated.set(activeConversationId, {
              jobId: job.jobId,
              prompt: job.prompt,
              status: job.status,
              startedAt: job.startedAt,
            });
            return updated;
          });
          setIsStreaming(true);
        }
      } catch (error) {
        console.error('[ASYNC-CHAT] Failed to check active jobs:', error);
      }
    };

    checkActiveJobs();
  }, [sessionId, activeConversationId, token, activeJobs]);

  // Sync isStreaming state with current conversation's active job status
  // This ensures switching conversations shows the correct loading state
  useEffect(() => {
    if (activeConversationId && activeJobs.has(activeConversationId)) {
      setIsStreaming(true);
      // Note: streamed content is loaded in checkActiveJobs effect
    } else {
      // Switching to a conversation without an active job - clear streaming state
      setIsStreaming(false);
      setStreamingContent('');
      streamingContentRef.current = '';
    }
  }, [activeConversationId, activeJobs]);

  // Helper function to build the complete system prompt for async chat
  const buildAsyncSystemPrompt = useCallback((
    transcript: string,
    contextContent: any[],
    sessionContextContent: string,
    fileContextContent: string,
    useStorytelling: boolean,
    disableTimecodeLinks: boolean
  ): string => {
    let systemPrompt = `You are a helpful AI assistant. You are having a conversation with a user about the following transcript.
---
${transcript}
---
**IMPORTANT:** Your task is to answer the user's questions and discuss the transcript.
- Do NOT generate NEW TTML, XML, or any other code format output.
- Focus on providing helpful, conversational answers about the transcript content in plain text and markdown.
- Use Markdown for formatting if it helps with readability.`;

    // Add timecode instructions if not disabled
    if (!disableTimecodeLinks) {
      systemPrompt += `

**CRITICAL: Time Code Citation Format**
When referencing specific moments in transcripts, you MUST use this interactive time code markup:

For single time points:
[snippet_name](timecode://host/HH:MM:SS?name=description)

For time ranges:
[snippet_name](timecode://host/HH:MM:SS/HH:MM:SS?name=description)

Format requirements:
- Always use HH:MM:SS format (e.g., 00:15:30, 01:02:45)
- Include a descriptive name parameter
- Make links descriptive, not just "click here"

Examples:
- [Guest discusses AI ethics](timecode://host/00:15:30?name=AI%20Ethics%20Discussion)
- [Key point about neural networks](timecode://host/00:25:00/00:27:30?name=Neural%20Networks)`;
    } else {
      systemPrompt += `

**TIME REFERENCES:**
When referencing specific moments, use plain text format like "at 12:34:56" or "from 12:34 to 15:45".
Do NOT use any special timecode links or markdown links for time references.`;
    }

    // Add context content (TTML, topic map, YouTube stats, comments)
    if (contextContent.length > 0) {
      systemPrompt += `\n\n**ADDITIONAL CONTEXT:**\n`;
      for (const ctx of contextContent) {
        systemPrompt += `\n### ${ctx.name}\n\`\`\`\n${ctx.content}\n\`\`\`\n`;
      }
    }

    // Add session context if any
    if (sessionContextContent) {
      systemPrompt += `\n\n${sessionContextContent}`;
    }

    // Add file context if any
    if (fileContextContent) {
      systemPrompt += `\n\n${fileContextContent}`;
    }

    // Add storytelling context if enabled
    if (useStorytelling) {
      systemPrompt += `\n\n**STORYTELLING FRAMEWORK:**\n\`\`\`\n${STORYTELLING_CONTEXT}\n\`\`\`

When analyzing content or providing editing recommendations, please apply these storytelling principles to help identify:
- Opportunities for stronger narrative structure
- Ways to enhance emotional engagement
- Techniques to improve pacing and tension
- Methods to create more compelling hooks and reveals
- Approaches to better connect with the audience`;
    }

    return systemPrompt;
  }, []);

  // Check if user has provided API key for public shares
  const hasValidApiKey = isPublicView ? BYOKService.hasApiKey() : true;

  // Duration calculation utilities
  
  // Listen for audio context changes to clear widget playing state
  useEffect(() => {
    if (!globalIsPlaying) {
      setPlayingWidgetId(null);
      playingSegmentEndRef.current = null;
    }
  }, [globalIsPlaying]);
  
  // Check if current time reached segment end
  // Commented out to prevent chat from syncing with audio player
  // useEffect(() => {
  //   if (playingSegmentEndRef.current && currentTime >= playingSegmentEndRef.current) {
  //     setPlayingWidgetId(null);
  //     playingSegmentEndRef.current = null;
  //     pause();
  //   }
  // }, [currentTime, pause]);
  
  // Time code handling functions
  const handleTimeCodePlay = useCallback(async (targetSessionId: string, startSeconds: number, endSeconds?: number) => {
    // Generate widget ID for this playback
    const widgetId = `widget_${targetSessionId}_${startSeconds}_${endSeconds || 'single'}`;
    
    // If clicking the same widget that's playing, stop it
    if (playingWidgetId === widgetId) {
      pause();
      setPlayingWidgetId(null);
      playingSegmentEndRef.current = null;
      return;
    }
    
    // Set the widget as playing
    setPlayingWidgetId(widgetId);
    playingSegmentEndRef.current = endSeconds || null;
    
    // Set the current playing time code for UI feedback
    const timeCodeId = `${targetSessionId}_${startSeconds}_${endSeconds || 'single'}`;
    setCurrentPlayingTimeCode(timeCodeId);
    
    console.log(`Playing time code: ${targetSessionId} from ${startSeconds}s${endSeconds ? ` to ${endSeconds}s` : ''}`);
    
    try {
      // Check if it's the current session
      if (targetSessionId === sessionId) {
        // Play the segment using the already loaded audio
        await playSegment(startSeconds, endSeconds);
      } else {
        // Need to load audio from a different session
        console.log(`[ChatInterface] Loading audio from different session: ${targetSessionId}`);
        
        if (token) {
          const targetSessionData = await sessionService.getSession(targetSessionId, token);
          if (targetSessionData.audioUrl) {
            // Load the new audio
            loadAudio(targetSessionData.audioUrl);
            
            // Wait a bit for audio to load, then play segment
            setTimeout(() => {
              playSegment(startSeconds, endSeconds);
            }, 500);
          } else {
            console.error('[ChatInterface] No audio URL available for session:', targetSessionId);
            setCurrentPlayingTimeCode(null);
            setPlayingWidgetId(null);
          }
        }
      }
    } catch (error) {
      console.error('[ChatInterface] Error playing time code:', error);
      setCurrentPlayingTimeCode(null);
      setPlayingWidgetId(null);
    }
  }, [sessionId, token, currentPlayingTimeCode, pause, loadAudio, playSegment, setPlayingWidgetId]);
  
  const handleTimeCodeStop = useCallback(() => {
    pause();
    setPlayingWidgetId(null);
    setCurrentPlayingTimeCode(null);
    playingSegmentEndRef.current = null;
  }, [pause, setPlayingWidgetId]);
  
  const handleHoverTimeRange = useCallback((startTime: number, endTime?: number) => {
    setHoveredTimeRange({ start: startTime, end: endTime });
  }, [setHoveredTimeRange]);
  
  const handleHoverEnd = useCallback(() => {
    setHoveredTimeRange(null);
  }, [setHoveredTimeRange]);

  const handleTimeCodeMaximize = (sessionId: string, startSeconds: number, endSeconds?: number) => {
    // Get session title from context if available
    const sessionItem = sessionContext.find(item => item.sessionId === sessionId);
    const sessionTitle = sessionItem?.title || `Session ${sessionId.substring(0, 8)}...`;
    
    setTimelineModalData({
      sessionId,
      startSeconds,
      endSeconds,
      sessionTitle
    });
    setIsTimelineModalOpen(true);
  };

  const handleLoadSession = useCallback((sessionId: string) => {
    // Open session in new tab
    window.open(`/session/${sessionId}`, '_blank');
  }, []);

  // Store callbacks in refs to avoid recreating the component
  const handleTimeCodePlayRef = useRef(handleTimeCodePlay);
  const handleTimeCodeStopRef = useRef(handleTimeCodeStop);
  const handleHoverTimeRangeRef = useRef(handleHoverTimeRange);
  const handleHoverEndRef = useRef(handleHoverEnd);
  
  useEffect(() => {
    handleTimeCodePlayRef.current = handleTimeCodePlay;
    handleTimeCodeStopRef.current = handleTimeCodeStop;
    handleHoverTimeRangeRef.current = handleHoverTimeRange;
    handleHoverEndRef.current = handleHoverEnd;
  }, [handleTimeCodePlay, handleTimeCodeStop, handleHoverTimeRange, handleHoverEnd]);

  const createTimeCodeComponent = useCallback(({ href, children, node, ...props }: any) => {
    // Debug logging commented out for performance
    // console.log('[TimeCodeComponent] Link component called:', { href, children });
    
    // Check for timecode in various forms (might be encoded)
    const isTimecodeLink = href && (
      href.startsWith('timecode://') || 
      href.includes('timecode://') ||
      href.includes('timecode%3A%2F%2F') // URL encoded version
    );
    
    if (isTimecodeLink) {
      // Extract text from children (could be string, array, or React element)
      const extractText = (node: any): string => {
        if (typeof node === 'string') return node;
        if (typeof node === 'number') return String(node);
        if (Array.isArray(node)) return node.map(extractText).join('');
        if (node?.props?.children) return extractText(node.props.children);
        if (node?.toString && typeof node.toString === 'function') {
          const str = node.toString();
          return str === '[object Object]' ? '' : str;
        }
        return '';
      };
      
      const linkText = extractText(children) || 'Audio Clip';
      
      // Decode href if needed, with error handling for malformed URIs
      let decodedHref = href;
      try {
        decodedHref = decodeURIComponent(href);
      } catch (e) {
        console.error('[TimeCodeComponent] Failed to decode URI:', href, e);
        // Use the original href if decoding fails
      }
      // console.log('[TimeCodeComponent] Processing timecode:', { originalHref: href, decodedHref, linkText });
      
      const timeCodeData = parseTimeCode(decodedHref, linkText);
      // console.log('[TimeCodeComponent] Parsed data:', timeCodeData);
      
      if (timeCodeData) {
        // Only replace 'host' with current session ID
        if (timeCodeData.sessionId === 'host' && sessionId) {
          // console.log('[TimeCodeComponent] Replacing host with sessionId:', sessionId);
          timeCodeData.sessionId = sessionId;
        } else if (timeCodeData.sessionId !== sessionId) {
          // console.log('[TimeCodeComponent] Different session ID detected');
          // Keep the original session ID if it's not 'host'
        }
        
        const timeCodeId = `${timeCodeData.sessionId}_${timeCodeData.startTime}_${timeCodeData.endTime || 'single'}`;
        const isPlaying = currentPlayingTimeCode === timeCodeId;
        const isCurrentSession = timeCodeData.sessionId === (sessionId || '');
        
        // Check if text-only mode is enabled
        if (textOnlyTimeReferences) {
          // Format: HH:MM:SS [description] or HH:MM:SS → HH:MM:SS [description]
          const timeDisplay = timeCodeData.endTime 
            ? `${timeCodeData.startTime} → ${timeCodeData.endTime}` 
            : timeCodeData.startTime;
          const sessionInfo = !isCurrentSession ? ` (Session ${timeCodeData.sessionId.substring(0, 8)}...)` : '';
          const textDisplay = `${timeDisplay} [${linkText}]${sessionInfo}`;
          
          return (
            <span 
              className={`${brandLinkClass} font-mono text-sm cursor-pointer`}
              onClick={() => {
                if (isCurrentSession) {
                  handleTimeCodePlayRef.current(timeCodeData.sessionId, timeToSeconds(timeCodeData.startTime), timeCodeData.endTime ? timeToSeconds(timeCodeData.endTime) : undefined);
                } else {
                  handleLoadSession(timeCodeData.sessionId);
                }
              }}
              title={timeCodeData.name ? (() => {
                try {
                  return decodeURIComponent(timeCodeData.name);
                } catch (e) {
                  console.error('[TimeCodeComponent] Failed to decode name:', timeCodeData.name, e);
                  return timeCodeData.name;
                }
              })() : 'Click to play'}
            >
              {textDisplay}
            </span>
          );
        }
        
        // Default widget view - create a unique key for memoization
        const widgetKey = `${timeCodeData.sessionId}_${timeCodeData.startTime}_${timeCodeData.endTime || 'single'}`;
        
        return (
          <div className="block my-2">
            <MemoizedAudioClipCard
              key={widgetKey}
              clipData={timeCodeData}
              onLoadSession={handleLoadSession}
              currentSessionId={sessionId}
              widgetId={widgetKey}
            />
          </div>
        );
      } else {
        // console.log('[TimeCodeComponent] Failed to parse timecode data');
      }
    }
    
    // Regular link
    return <a href={href} {...props} className={brandLinkClass}>{children}</a>;
  }, [brandLinkClass, sessionId, currentPlayingTimeCode, handleLoadSession, textOnlyTimeReferences]); // Removed unstable deps
  
  // Memoized message component to prevent re-renders during audio playback
  const ChatMessage = memo(({ 
    message, 
    index, 
    isDarkMode,
    isEditing,
    editContent,
    onEdit,
    onSave,
    onCancel,
    onSetEdit,
    onContentChange,
    ttmlFiles,
    onDownloadTtml
  }: {
    message: { role: string; content: string };
    index: number;
    isDarkMode: boolean;
    isEditing: boolean;
    editContent: string;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onSetEdit: (content: string) => void;
    onContentChange: (content: string) => void;
    ttmlFiles: any[];
    onDownloadTtml: (file: any) => void;
  }) => (
    <div className={`flex items-start space-x-3 ${
      message.role === 'user' ? 'justify-end' : 'justify-start'
    }`}>
      {message.role === 'assistant' && (
        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
          <Bot size={16} className="text-primary-600" />
        </div>
      )}
      
      <div
        className={`max-w-[70%] p-3 rounded-lg ${
          message.role === 'user'
            ? 'bg-primary-600 text-white ml-auto'
            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 dark:text-gray-100'
        }`}
      >
        <div className="markdown-content">
          {message.role === 'user' ? (
            isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editContent}
                  onChange={(e) => onContentChange(e.target.value)}
                  className="w-full p-2 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:border-white/40"
                  rows={4}
                />
                <div className="flex gap-2">
                  <button
                    onClick={onSave}
                    className="px-3 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={onCancel}
                    className="px-3 py-1 text-xs text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap">{message.content}</div>
            )
          ) : (
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                urlTransform={(url) => {
                  if (url.startsWith('timecode://')) {
                    return url;
                  }
                  return url;
                }}
                components={markdownComponents}
              >
                {removeCodeBlocks(message.content)}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* TTML Download Buttons */}
        {message.role === 'assistant' && ttmlFiles.filter(f => f.messageIndex === index).length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-600 mb-2">TTML Files Generated:</div>
            <div className="flex flex-wrap gap-2">
              {ttmlFiles.filter(f => f.messageIndex === index).map((file) => (
                <button
                  key={file.id}
                  onClick={() => onDownloadTtml(file)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors border border-[#FFB52B]/40 bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1] dark:hover:bg-[#FFB52B]/[0.24]"
                >
                  <FileText size={12} />
                  {file.filename || 'Download TTML'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {message.role === 'user' && (
        <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
          <User size={16} className="text-gray-600" />
        </div>
      )}
    </div>
  ));

  
  // Conversation management functions
  const loadConversationsFromSession = async () => {
    if (!sessionId || (!token && !shareToken)) return;

    try {
      // For public share users, load conversations from localStorage (privacy-preserving)
      if (isPublicView) {
        const localConversations = getPublicShareConversations();
        const activeConvId = getPublicShareActiveConversationId();

        if (localConversations.length > 0) {
          setConversations(localConversations);
          const activeId = activeConvId && localConversations.find(c => c.id === activeConvId)
            ? activeConvId
            : localConversations[0].id;
          setActiveConversationId(activeId);

          const activeConv = localConversations.find(c => c.id === activeId);
          if (activeConv) {
            setMessages(activeConv.messages.map(msg => ({ role: msg.role as 'user' | 'assistant', content: msg.content })));
            loadConversationSettings(activeConv);
          }
          console.log('[CHAT] Loaded public share conversations from localStorage:', localConversations.length);
        } else {
          // No local conversations, create first one
          await handleCreateConversation();
        }
        return;
      }

      // For logged-in users, load from server
      const sessionData = await sessionService.getSession(sessionId, token || shareToken!);

      // Filter to only show non-public-share conversations for logged-in users
      const filterConversations = (convs: ChatConversation[]): ChatConversation[] => {
        if (!convs) return [];
        // Logged-in users see conversations without isPublicShare flag
        return convs.filter(c => !c.isPublicShare);
      };

      // Migrate legacy data to conversation format if needed
      if (!sessionData.conversations || sessionData.conversations.length === 0) {
        if (sessionData.chatMessages && sessionData.chatMessages.length > 0) {
          // Create default conversation from legacy messages (only for logged-in users)
          const defaultConversation: ChatConversation = {
            id: 'default',
            name: 'Main Conversation',
            messages: sessionData.chatMessages.map(msg => ({
              ...msg,
              timestamp: new Date()
            })),
            chatSettings: sessionData.chatSettings,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          setConversations([defaultConversation]);
          setActiveConversationId('default');
          setMessages(sessionData.chatMessages.map(msg => ({
            role: msg.role as 'user' | 'assistant',
            content: msg.content
          })));


          // Load conversation-specific settings
          loadConversationSettings(defaultConversation);
        } else {
          // No existing data, create first conversation
          await handleCreateConversation();
        }
      } else {
        // Filter conversations for current user
        const userConversations = filterConversations(sessionData.conversations);

        if (userConversations.length === 0) {
          // No conversations for this user, create new one
          await handleCreateConversation();
        } else {
          // Load existing conversations
          setConversations(userConversations);

          // Find the active conversation for this user
          const activeConvId = userConversations.find(c => c.id === sessionData.activeConversationId)?.id || userConversations[0]?.id;
          setActiveConversationId(activeConvId || null);

          // Load active conversation data
          const activeConv = userConversations.find(c => c.id === activeConvId);
          if (activeConv) {
            setMessages(activeConv.messages.map(msg => ({ role: msg.role as 'user' | 'assistant', content: msg.content })));


            // Load conversation-specific settings
            loadConversationSettings(activeConv);
          }
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      // Create first conversation on error
      await handleCreateConversation();
    }
  };

  // Save conversation data to session (or localStorage for public share users)
  const saveConversationToSession = async (conversation: ChatConversation) => {
    if (!sessionId || (!token && !shareToken)) return;

    try {
      // For public share users, save to localStorage (privacy-preserving, ephemeral)
      if (isPublicView) {
        const localConversations = getPublicShareConversations();
        const conversationIndex = localConversations.findIndex(c => c.id === conversation.id);
        if (conversationIndex >= 0) {
          localConversations[conversationIndex] = conversation;
        } else {
          localConversations.push(conversation);
        }
        savePublicShareConversations(localConversations);
        savePublicShareActiveConversationId(conversation.id);
        return;
      }

      // For logged-in users, save to server
      const sessionData = await sessionService.getSession(sessionId, token || shareToken!);
      const allConversations = sessionData.conversations || [];

      // Update or add the current conversation
      const conversationIndex = allConversations.findIndex(c => c.id === conversation.id);
      if (conversationIndex >= 0) {
        allConversations[conversationIndex] = conversation;
      } else {
        allConversations.push(conversation);
      }

      await sessionService.updateSession({
        sessionId,
        conversations: allConversations,
        activeConversationId: conversation.id
      }, token || shareToken!);
    } catch (error) {
      console.error('Error saving conversation to session:', error);
    }
  };

  // Save current conversation state to session
  // Pass messagesToSave to avoid stale closure issues when called after setMessages
  const saveCurrentConversationState = async (updateTimestamp = true, messagesToSave?: ChatMessage[]) => {
    if (!activeConversationId || !sessionId || (!token && !shareToken)) {
      console.warn('[CHAT] Cannot save conversation state - missing activeConversationId, sessionId, or token/shareToken');
      return;
    }

    const currentConversation = conversations.find(c => c.id === activeConversationId);
    if (!currentConversation) {
      console.warn('[CHAT] Cannot save conversation state - current conversation not found');
      return;
    }

    // Use passed messages if provided, otherwise fall back to state (may be stale in closures)
    const currentMessages = messagesToSave || messages;
    console.log('[CHAT] Saving conversation state for:', activeConversationId, 'updateTimestamp:', updateTimestamp, 'messageCount:', currentMessages.length);

    // Update conversation with current state
    const updatedConversation: ChatConversation = {
      ...currentConversation,
      messages: currentMessages.map(msg => ({ ...msg, timestamp: new Date() })),
      chatSettings: saveCurrentConversationSettings(),
      ...(updateTimestamp && { updatedAt: new Date() })
    };

    // Update conversations array
    setConversations(prev => prev.map(conv =>
      conv.id === activeConversationId ? updatedConversation : conv
    ));

    // Save to session
    try {
      await saveConversationToSession(updatedConversation);
      console.log('[CHAT] Successfully saved conversation state with', currentMessages.length, 'messages');
    } catch (error) {
      console.error('[CHAT] Failed to save conversation state:', error);
    }
  };

  // Check if conversation needs auto-naming after first AI response
  const checkAndAutoNameConversation = async (conversationMessages: ChatMessage[]) => {
    if (!activeConversationId || !sessionId) return;

    // For public shares, require API key to be set; for logged-in users, require token
    if (isPublicView) {
      if (!BYOKService.getApiKey()) return;
    } else {
      if (!token) return;
    }

    const currentConversation = conversations.find(c => c.id === activeConversationId);
    if (!currentConversation || currentConversation.name !== 'New Conversation') return;

    // Count messages - auto-name after first AI response (1 user + 1 assistant)
    const humanMessageCount = conversationMessages.filter(msg => msg.role === 'user').length;
    const assistantMessageCount = conversationMessages.filter(msg => msg.role === 'assistant').length;

    // Auto-name after first complete exchange (1 user message + 1 assistant response)
    if (humanMessageCount >= 1 && assistantMessageCount >= 1) {
      await handleGenerateConversationName(activeConversationId);
    }
  };

  const handleCreateConversation = async () => {
    if (!sessionId || (!token && !shareToken)) return;

    try {
      // Determine owner ID: use token hash for logged-in users, browser ID for public share users
      const ownerId = isPublicView ? BrowserIdService.getBrowserId() : (token ? token.substring(0, 32) : undefined);

      // Create conversation locally without backend dependency
      const newConversation: ChatConversation = {
        id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: 'New Conversation',
        messages: [],
        ownerId, // Track who owns this conversation
        isPublicShare: isPublicView, // Mark if created by public share user
        chatSettings: {
          // Inherit most settings from current conversation, but NOT user-specific files
          selectedModel: model,
          useStorytellingContext,
          enableWebSearch,
          textOnlyTimeReferences,
          // Start with empty files for new conversations - files are user-specific
          uploadedFiles: [],
          fileContextToggles: {},
          sessionContext,
          includeTTMLInContext,
          includeTopicMapInContext,
          includeCommentsInContext,
          includeYouTubeStatsInContext
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setConversations(prev => [...prev, newConversation]);
      setActiveConversationId(newConversation.id);
      setMessages([]);
      // Clear files when starting a new conversation
      setUploadedFiles([]);
      setFileContextToggles({});


      // Save conversation to session data
      await saveConversationToSession(newConversation);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  // Helper functions to manage conversation-specific settings
  const saveCurrentConversationSettings = () => {
    return {
      selectedModel: model,
      useStorytellingContext,
      enableWebSearch,
      textOnlyTimeReferences,
      uploadedFiles,
      fileContextToggles,
      sessionContext,
      includeTTMLInContext,
      includeTopicMapInContext,
      includeCommentsInContext,
      includeYouTubeStatsInContext
    };
  };

  const loadConversationSettings = (conversation: ChatConversation) => {
    const settings = conversation.chatSettings;

    // Determine current user's owner ID
    const currentOwnerId = isPublicView ? BrowserIdService.getBrowserId() : (token ? token.substring(0, 32) : undefined);

    // Check if this conversation belongs to the current user
    const isOwnConversation = !conversation.ownerId || conversation.ownerId === currentOwnerId;

    if (settings) {
      // Update model through parent component callback
      if (settings.selectedModel && settings.selectedModel !== model) {
        onModelChange(settings.selectedModel);
      }

      // Update local state settings
      setUseStorytellingContext(settings.useStorytellingContext ?? false);
      setEnableWebSearch(settings.enableWebSearch ?? false);
      setTextOnlyTimeReferences(settings.textOnlyTimeReferences ?? false);

      // Update context inclusion settings
      if (settings.includeTTMLInContext !== undefined) {
        setIncludeTTMLInContext(settings.includeTTMLInContext);
      }
      if (settings.includeTopicMapInContext !== undefined) {
        setIncludeTopicMapInContext(settings.includeTopicMapInContext);
      }
      if (settings.includeCommentsInContext !== undefined) {
        setIncludeCommentsInContext(settings.includeCommentsInContext);
      }
      if (settings.includeYouTubeStatsInContext !== undefined) {
        setIncludeYouTubeStatsInContext(settings.includeYouTubeStatsInContext);
      }

      // Only load file-related settings if this is the user's own conversation
      // This prevents files from leaking between users
      if (isOwnConversation) {
        setUploadedFiles(settings.uploadedFiles ?? []);
        setFileContextToggles(settings.fileContextToggles ?? {});
      } else {
        // Clear files when viewing another user's conversation
        setUploadedFiles([]);
        setFileContextToggles({});
      }

      // Only update sessionContext if the conversation specifically has it defined
      // This preserves session-level sessionContext for existing conversations
      if (settings.sessionContext !== undefined) {
        setSessionContext(settings.sessionContext);
      }
    } else {
      // Set defaults for conversations without settings
      setUseStorytellingContext(false);
      setEnableWebSearch(false);
      setTextOnlyTimeReferences(false);
      setUploadedFiles([]);
      setFileContextToggles({});
      // Don't reset context inclusion settings - preserve session-level defaults
    }
  };

  // Cancel an active async job for the current conversation
  const handleCancelJob = async () => {
    if (!sessionId || !token || !activeConversationId) return;

    const activeJob = activeJobs.get(activeConversationId);
    if (!activeJob) return;

    try {
      console.log('[ASYNC-CHAT] Cancelling job:', activeJob.jobId);
      const result = await SessionService.cancelAsyncChatJob(sessionId, activeJob.jobId, token);

      if (result.success) {
        // Remove the job from active jobs
        setActiveJobs(prev => {
          const updated = new Map(prev);
          updated.delete(activeConversationId);
          return updated;
        });
        setIsStreaming(false);

        // Remove the last user message that was waiting for a response
        setMessages(prev => {
          if (prev.length > 0 && prev[prev.length - 1].role === 'user') {
            return prev.slice(0, -1);
          }
          return prev;
        });

        console.log('[ASYNC-CHAT] Job cancelled successfully');
      } else {
        console.error('[ASYNC-CHAT] Failed to cancel job:', result.error);
        setError(result.error || 'Failed to cancel job');
      }
    } catch (error) {
      console.error('[ASYNC-CHAT] Error cancelling job:', error);
      setError('Failed to cancel job');
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    if (!sessionId || (!token && !shareToken) || conversationId === activeConversationId) return;

    // Save current conversation state before switching (without updating timestamp)
    await saveCurrentConversationState(false);

    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setActiveConversationId(conversationId);
      setMessages(conversation.messages.map(msg => ({ role: msg.role as 'user' | 'assistant', content: msg.content })));

      // Load conversation-specific settings
      loadConversationSettings(conversation);

      // Set as active conversation (localStorage for public share, backend for logged-in)
      if (isPublicView) {
        savePublicShareActiveConversationId(conversationId);
      } else if (token) {
        await sessionService.setActiveConversation(sessionId, conversationId, token);
      }
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (!sessionId || (!token && !shareToken)) return;

    try {
      const isLastConversation = conversations.length === 1;

      if (isLastConversation) {
        // When deleting the last conversation, create a new one first
        // IMPORTANT: Clear files when deleting last conversation - start fresh
        setUploadedFiles([]);
        setFileContextToggles({});

        const newConversation: ChatConversation = {
          id: `conv_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
          name: 'New Conversation',
          messages: [],
          createdAt: new Date(),
          updatedAt: new Date(),
          isPinned: false,
          isPublicShare: isPublicView,
          ownerId: isPublicView ? BrowserIdService.getBrowserId() : undefined,
          chatSettings: {
            selectedModel: model,
            useStorytellingContext,
            enableWebSearch,
            textOnlyTimeReferences,
            uploadedFiles: [], // Start fresh with no files
            fileContextToggles: {},
            sessionContext,
            includeTTMLInContext,
            includeTopicMapInContext,
            includeCommentsInContext,
            includeYouTubeStatsInContext
          }
        };

        // Replace the conversation array with just the new conversation
        setConversations([newConversation]);
        setActiveConversationId(newConversation.id);
        setMessages([]);

        // Save to localStorage for public share users, or backend for logged-in users
        if (isPublicView) {
          savePublicShareConversations([newConversation]);
          savePublicShareActiveConversationId(newConversation.id);
        } else if (token) {
          await sessionService.updateSession({
            sessionId,
            conversations: [newConversation],
            activeConversationId: newConversation.id
          }, token);
        }

        console.log('[CHAT] Deleted last conversation and created new one:', newConversation.id);
      } else {
        // Normal delete - more than one conversation
        const updatedConversations = conversations.filter(c => c.id !== conversationId);
        setConversations(updatedConversations);

        // Save to localStorage for public share users, or backend for logged-in users
        if (isPublicView) {
          savePublicShareConversations(updatedConversations);
          // If deleting active conversation, switch to first available
          if (conversationId === activeConversationId) {
            const newActiveId = updatedConversations[0]?.id;
            if (newActiveId) {
              savePublicShareActiveConversationId(newActiveId);
              await handleSelectConversation(newActiveId);
            }
          }
        } else if (token) {
          await sessionService.deleteConversation(sessionId, conversationId, token);
          // If deleting active conversation, switch to first available
          if (conversationId === activeConversationId) {
            const newActiveId = updatedConversations[0]?.id;
            if (newActiveId) {
              await handleSelectConversation(newActiveId);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleRenameConversation = async (conversationId: string, newName: string) => {
    if (!sessionId || (!token && !shareToken)) return;

    try {
      const updatedConversations = conversations.map(c =>
        c.id === conversationId ? { ...c, name: newName, updatedAt: new Date() } : c
      );
      setConversations(updatedConversations);

      // Save to localStorage for public share users, or backend for logged-in users
      if (isPublicView) {
        savePublicShareConversations(updatedConversations);
      } else if (token) {
        await sessionService.updateConversation(sessionId, conversationId, { name: newName }, token);
      }
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  };

  const handleGenerateConversationName = async (conversationId: string) => {
    if (!sessionId) return;

    // For public shares, use BYOK; for authenticated users, use app's key
    const userOpenRouterKey = isPublicView ? BYOKService.getApiKey() : null;

    // For public shares, require API key
    if (isPublicView && !userOpenRouterKey) {
      console.log('Cannot generate name: No API key available for public share');
      return;
    }

    setIsGeneratingConversationName(conversationId);
    try {
      const conversation = conversations.find(c => c.id === conversationId);
      if (!conversation || conversation.messages.length === 0) return;

      // Only use human messages for naming to avoid transcript content dominating
      const humanMessages = conversation.messages
        .filter(msg => msg.role === 'user')
        .map(msg => msg.content)
        .join(' ')
        .substring(0, 500); // Limit to 500 chars for efficiency

      if (!humanMessages.trim()) {
        // If no human messages, fall back to simple naming
        const updatedConversation: ChatConversation = {
          ...conversation,
          name: 'Chat Conversation',
          updatedAt: new Date()
        };

        setConversations(prev => prev.map(c =>
          c.id === conversationId ? updatedConversation : c
        ));

        await saveConversationToSession(updatedConversation);
        return;
      }

      const response = await openRouterService.chatWithTranscript({
        model: 'anthropic/claude-sonnet-4',
        message: `Generate a concise 4-5 word title for this conversation based ONLY on what the human has asked or discussed. Be descriptive and instructive. Focus on the main topic or action the human is asking about.

Human messages:
${humanMessages}

Respond with ONLY the title, nothing else.`,
        transcript: humanMessages || 'Conversation', // Use human messages as transcript to satisfy API requirement
        conversationHistory: []
      }, token || undefined, userOpenRouterKey);

      const generatedName = response.response.trim().replace(/['"]/g, ''); // Remove quotes

      const updatedConversation: ChatConversation = {
        ...conversation,
        name: generatedName,
        updatedAt: new Date()
      };

      setConversations(prev => prev.map(c =>
        c.id === conversationId ? updatedConversation : c
      ));

      // Save to session
      await saveConversationToSession(updatedConversation);
    } catch (error) {
      console.error('Error generating conversation name:', error);
    } finally {
      setIsGeneratingConversationName(null);
    }
  };

  const handleDuplicateConversation = async (conversationId: string) => {
    if (!sessionId || !token) return;
    
    try {
      const conversation = conversations.find(c => c.id === conversationId);
      if (!conversation) return;
      
      // Create a duplicate conversation
      const duplicatedConversation: ChatConversation = {
        id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: `${conversation.name} (Copy)`,
        messages: [...conversation.messages],
        chatSettings: conversation.chatSettings ? { ...conversation.chatSettings } : undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
        isPinned: false
      };
      
      setConversations(prev => [...prev, duplicatedConversation]);
      setActiveConversationId(duplicatedConversation.id);
      
      // Load the duplicated conversation
      loadConversationSettings(duplicatedConversation);
      setMessages(duplicatedConversation.messages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })));
      
      // Save to session
      await saveConversationToSession(duplicatedConversation);
    } catch (error) {
      console.error('Error duplicating conversation:', error);
    }
  };

  const handleTogglePinConversation = async (conversationId: string) => {
    if (!sessionId || !token) return;
    
    try {
      const conversation = conversations.find(c => c.id === conversationId);
      if (!conversation) return;
      
      const updatedConversation: ChatConversation = {
        ...conversation,
        isPinned: !conversation.isPinned,
        updatedAt: new Date()
      };
      
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? updatedConversation : c
      ));
      
      // Save to session
      await saveConversationToSession(updatedConversation);
    } catch (error) {
      console.error('Error toggling pin status:', error);
    }
  };

  // Function to estimate transcript duration and abbreviate if > 2 minutes
  const processTranscriptForContext = (transcript: string, wordCount: number): string => {
    if (!transcript) return transcript;
    
    // Estimate duration: average speaking rate is ~150 words per minute
    const estimatedMinutes = wordCount / 150;
    
    if (estimatedMinutes <= 2) {
      // Short enough - use full transcript
      return transcript;
    }
    
    // Too long - create abbreviated version
    const lines = transcript.split('\n');
    const totalLines = lines.length;
    const sampleSize = Math.max(10, Math.floor(totalLines * 0.2)); // Take 20% or minimum 10 lines
    
    const beginningLines = lines.slice(0, Math.floor(sampleSize / 2));
    const endingLines = lines.slice(-Math.floor(sampleSize / 2));
    
    const abbreviatedTranscript = [
      `[ABBREVIATED TRANSCRIPT - Estimated ${estimatedMinutes.toFixed(1)} minutes]`,
      '',
      '[BEGINNING EXCERPT]',
      ...beginningLines,
      '',
      `[... MIDDLE CONTENT ABBREVIATED (${totalLines - sampleSize} lines omitted) ...]`,
      '',
      '[ENDING EXCERPT]', 
      ...endingLines,
      '',
      `[Note: This transcript has been abbreviated. Original length: ${wordCount} words, ~${estimatedMinutes.toFixed(1)} minutes. LLM should summarize key points rather than quote verbatim.]`
    ].join('\n');
    
    console.log(`[CHAT] Abbreviated transcript from ${wordCount} words (${estimatedMinutes.toFixed(1)}min) to ${abbreviatedTranscript.split(' ').length} words`);
    
    return abbreviatedTranscript;
  };
  




  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    id: string;
    name: string;
    type: 'text' | 'pdf' | 'image';
    content?: string; // base64 for images/PDFs, text content for text files - may be loaded on demand
    size: number;
    mimeType: string;
    uploadedAt: string;
    s3Key?: string;
    isLoading?: boolean; // For files being loaded from S3
    includedInContext: boolean; // New field to track if file is included in chat context
  }>>([]);
  const [fileContextToggles, setFileContextToggles] = useState<Record<string, boolean>>({});
  const [sessionContext, setSessionContext] = useState<SessionContextItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
  const [editingMessageContent, setEditingMessageContent] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Don't auto-scroll if audio is playing from a widget
    if (playingWidgetId) return;
    
    // Only scroll to bottom for new messages, not re-renders
    const shouldScroll = messagesEndRef.current && 
      messages.length > 0 && 
      messages[messages.length - 1]?.role === 'assistant';
    
    if (shouldScroll) {
      scrollToBottom();
    }
  }, [messages.length]); // Only depend on length, not content

  // Load conversations on mount
  useEffect(() => {
    loadConversationsFromSession();
  }, [sessionId, token]);

  // Save conversation state when messages or storyboard content changes
  useEffect(() => {
    if (activeConversationId && messages.length > 0) {
      const timeoutId = setTimeout(() => {
        saveCurrentConversationState();
      }, 1000); // Debounce saves

      return () => clearTimeout(timeoutId);
    }
  }, [messages]);

  // Extract TTML blocks from message content
  const extractTTMLBlocks = (content: string, messageIndex: number): TTMLFile[] => {
    console.log(`[CHAT] extractTTMLBlocks called with messageIndex: ${messageIndex}`);
    console.log(`[CHAT] Content to search:`, content);
    
    const blocks: TTMLFile[] = [];

    // Method 1: Look for traditional ```ttml blocks
    const ttmlRegex = /```ttml\n([\s\S]*?)\n```/g;
    let match;

    while ((match = ttmlRegex.exec(content)) !== null) {
      console.log(`[CHAT] Found traditional TTML block:`, match[1].substring(0, 100));
      const ttmlContent = match[1].trim();
      if (ttmlContent) {
        // Generate more descriptive ID based on TTML content
        const getTrackType = (content: string): string => {
          if (content.includes('r_overview') || content.includes('d_overview')) return 'overview';
          if (content.includes('r_clips') || content.includes('d_clips')) return 'clips';
          if (content.includes('r_best') || content.includes('d_best')) return 'best-parts';
          if (content.includes('r_weak') || content.includes('d_weak')) return 'weak-parts';
          if (content.includes('r_summary') || content.includes('d_summary')) return 'summary';
          if (content.includes('r_clip') || content.includes('d_clip')) return 'clip';
          if (content.includes('r_edit') || content.includes('d_edit')) return 'edit';
          return 'ttml';
        };

        const trackType = getTrackType(ttmlContent);
        const block = {
          id: `${trackType}-${messageIndex}-${blocks.length}`,
          content: ttmlContent,
          messageIndex,
          createdAt: new Date()
        };
        console.log(`[CHAT] Created TTML block:`, block.id, 'for messageIndex:', messageIndex);
        blocks.push(block);
      }
    }

    // Method 2: Look for TTML XML content directly (regardless of filename formatting)
    // Find all <tt>...</tt> blocks in the content
    const ttmlXmlRegex = /<tt[^>]*>[\s\S]*?<\/tt>/g;
    let xmlMatch;

    while ((xmlMatch = ttmlXmlRegex.exec(content)) !== null) {
      const ttmlContent = xmlMatch[0].trim();
      console.log(`[CHAT] Found TTML XML block:`, ttmlContent.substring(0, 100));
      
      // Generate a descriptive filename based on content or use generic name
      const generateTtmlFilename = (content: string, index: number): string => {
        // Try to extract any description from the content
        if (content.includes('r_overview') || content.includes('d_overview')) return 'overview.ttml';
        if (content.includes('r_clips') || content.includes('d_clips')) return 'clips.ttml';
        if (content.includes('r_best') || content.includes('d_best')) return 'best-parts.ttml';
        if (content.includes('r_weak') || content.includes('d_weak')) return 'weak-parts.ttml';
        if (content.includes('r_summary') || content.includes('d_summary')) return 'summary.ttml';
        if (content.includes('script_clips')) return `script-clips-${index + 1}.ttml`;
        if (content.includes('doom_debate')) return 'doom-debate.ttml';
        if (content.includes('main_interview')) return 'main-interview.ttml';
        if (content.includes('patreon_warmup')) return 'patreon-warmup.ttml';
        return `ttml-file-${index + 1}.ttml`;
      };

      const filename = generateTtmlFilename(ttmlContent, blocks.length);
      const baseId = filename.replace('.ttml', '');
      
      const block = {
        id: `${baseId}-${messageIndex}-${blocks.length}`,
        content: ttmlContent,
        messageIndex,
        createdAt: new Date(),
        filename: filename
      };
      console.log(`[CHAT] Created TTML block with filename: ${filename}`);
      blocks.push(block);
    }

    console.log(`[CHAT] Total TTML blocks found: ${blocks.length}`);
    return blocks;
  };

  // Download TTML file
  const downloadTTML = (ttmlFile: TTMLFile) => {
    const blob = new Blob([ttmlFile.content], { type: 'application/ttml+xml' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    
    // Use provided filename or generate one from ID
    let filename = ttmlFile.filename || `${ttmlFile.id}.ttml`;
    link.download = filename.endsWith('.ttml') ? filename : `${filename}.ttml`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  // Remove TTML blocks from display content
  const removeCodeBlocks = (content: string): string => {
    return content.replace(/```ttml\n[\s\S]*?\n```/g, '').trim();
  };
  
  // Debug function to check for timecode links
  const debugContent = (_content: string): void => {
    // Debug function disabled to prevent re-renders
    return;
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isFullscreen && event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // Set focus to the modal when opened
      const modalElement = document.querySelector('[role="dialog"]') as HTMLElement;
      if (modalElement) {
        modalElement.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsStreaming(false);
      setStreamingContent('');
      streamingContentRef.current = '';
    }
  };

  const handleEditMessage = (index: number) => {
    const message = messages[index];
    if (message.role === 'user') {
      setEditingMessageIndex(index);
      setEditingMessageContent(message.content);
    }
  };

  const handleSaveEdit = async (index: number) => {
    if (editingMessageContent.trim() && index !== null) {
      // Create new messages array with the edited message
      const updatedMessages = messages.slice(0, index);
      updatedMessages.push({ role: 'user' as const, content: editingMessageContent.trim() });
      
      // Update messages and clear editing state
      setMessages(updatedMessages);
      setEditingMessageIndex(null);
      setEditingMessageContent('');
      
      // Save to conversation
      if (activeConversationId) {
        const conversation = conversations.find(c => c.id === activeConversationId);
        if (conversation) {
          const updatedConversation = {
            ...conversation,
            messages: updatedMessages.map(msg => ({ ...msg, timestamp: new Date() })),
            updatedAt: new Date()
          };
          setConversations(prev => prev.map(c => 
            c.id === activeConversationId ? updatedConversation : c
          ));
          await saveConversationToSession(updatedConversation);
        }
      }
      
      // Re-execute from this point
      await executeFromMessage(index);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageIndex(null);
    setEditingMessageContent('');
  };

  const handleDeleteMessage = async (index: number) => {
    // Delete only this specific message
    const updatedMessages = messages.filter((_, i) => i !== index);
    setMessages(updatedMessages);
    
    // Save to conversation
    if (activeConversationId) {
      const conversation = conversations.find(c => c.id === activeConversationId);
      if (conversation) {
        const updatedConversation: ChatConversation = {
          ...conversation,
          messages: updatedMessages.map(msg => ({ ...msg, timestamp: new Date() })),
          updatedAt: new Date()
        };
        setConversations(prev => prev.map(c => 
          c.id === activeConversationId ? updatedConversation : c
        ));
        await saveConversationToSession(updatedConversation);
      }
    }
  };

  const handleExecuteMessage = async (index: number) => {
    const message = messages[index];
    
    if (message.role === 'user') {
      // For user messages, delete all subsequent messages and re-execute
      const updatedMessages = messages.slice(0, index + 1);
      setMessages(updatedMessages);
      
      // Save to conversation
      if (activeConversationId) {
        const conversation = conversations.find(c => c.id === activeConversationId);
        if (conversation) {
          const updatedConversation = {
            ...conversation,
            messages: updatedMessages.map(msg => ({ ...msg, timestamp: new Date() })),
            updatedAt: new Date()
          };
          setConversations(prev => prev.map(c => 
            c.id === activeConversationId ? updatedConversation : c
          ));
          await saveConversationToSession(updatedConversation);
        }
      }
      
      await executeFromMessage(index);
    } else {
      // For assistant messages, delete this message and all subsequent, then re-execute from previous user message
      const messagesBeforeIndex = messages.slice(0, index);
      let lastUserMessageIndex = -1;
      for (let i = messagesBeforeIndex.length - 1; i >= 0; i--) {
        if (messagesBeforeIndex[i].role === 'user') {
          lastUserMessageIndex = i;
          break;
        }
      }
      if (lastUserMessageIndex >= 0) {
        const updatedMessages = messages.slice(0, lastUserMessageIndex + 1);
        setMessages(updatedMessages);
        
        // Save to conversation
        if (activeConversationId) {
          const conversation = conversations.find(c => c.id === activeConversationId);
          if (conversation) {
            const updatedConversation: ChatConversation = {
              ...conversation,
              messages: updatedMessages.map(msg => ({ ...msg, timestamp: new Date() })),
              updatedAt: new Date()
            };
            setConversations(prev => prev.map(c => 
              c.id === activeConversationId ? updatedConversation : c
            ));
            await saveConversationToSession(updatedConversation);
          }
        }
        
        await executeFromMessage(lastUserMessageIndex);
      }
    }
  };

  const executeFromMessage = async (userMessageIndex: number) => {
    const userMessage = messages[userMessageIndex];
    if (userMessage.role !== 'user') return;
    
    // Execute directly without adding the message again (it's already in the conversation)
    await sendMessageFromIndex(userMessageIndex);
  };

  const sendMessageFromIndex = async (fromIndex: number) => {
    if (isLoading || isStreaming || !transcript) return;
    
    // Use messages up to and including the specified index
    const messagesToSend = messages.slice(0, fromIndex + 1);
    
    setError('');
    setStreamingContent('');
    setIsStreaming(true);
    streamingContentRef.current = '';
    
    // Create AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Convert existing messages to the format expected by the streaming API
      const chatMessages = messagesToSend.map(msg => ({ role: msg.role, content: msg.content }));

      // Get all current TTML content if available and enabled
      console.log('[CHAT] Current TTML files:', currentTtmlFiles);
      console.log('[CHAT] Include TTML in context:', includeTTMLInContext);
      let ttmlContent = (currentTtmlFiles.length > 0 && includeTTMLInContext)
        ? JSON.stringify(currentTtmlFiles.map(file => ({ name: file.name, content: file.content }))) 
        : undefined;
      console.log('[CHAT] TTML content being sent:', ttmlContent ? 'Present' : 'Not present');
      
      // Get narrative/topic map content if available and enabled
      console.log('[CHAT] Narrative data available:', !!narrativeData);
      console.log('[CHAT] Include topic map in context:', includeTopicMapInContext);

      // Combine TTML and topic map content into a single context object
      let contextContent: any[] = [];
      if (currentTtmlFiles.length > 0 && includeTTMLInContext) {
        contextContent.push(...currentTtmlFiles.map(file => ({
          type: 'ttml',
          name: file.name,
          content: file.content
        })));
      }
      if (narrativeData && includeTopicMapInContext) {
        console.log('[CHAT] Adding narrative/topic map to context, data keys:', Object.keys(narrativeData));
        contextContent.push({
          type: 'topicMap',
          name: 'Narrative Structure / Topic Map',
          content: JSON.stringify(narrativeData, null, 2)
        });
      } else if (includeTopicMapInContext && !narrativeData) {
        console.warn('[CHAT] Topic map toggle is ON but narrativeData is not available yet');
      }

      // Add YouTube retention data and stats if available and enabled
      if (youtubeRetentionData && includeYouTubeStatsInContext) {
        const ytStats = youtubeRetentionData.videoStats;
        const retentionPoints = youtubeRetentionData.retentionData;

        // Format retention data for context
        const formattedYouTubeData = {
          videoId: youtubeRetentionData.videoId,
          videoDurationSeconds: youtubeRetentionData.videoDurationSeconds,
          fetchedAt: youtubeRetentionData.fetchedAt,
          stats: ytStats ? {
            views: ytStats.views,
            likes: ytStats.likes,
            comments: ytStats.comments,
            shares: ytStats.shares,
            subscribersGained: ytStats.subscribersGained,
            subscribersLost: ytStats.subscribersLost,
            estimatedMinutesWatched: ytStats.estimatedMinutesWatched,
            averageViewDuration: ytStats.averageViewDuration,
            averageViewPercentage: ytStats.averageViewPercentage,
            videoTitle: ytStats.videoTitle,
            videoPublishedAt: ytStats.videoPublishedAt
          } : null,
          // Include all retention data points for fine-grained analysis
          // Each point has timestampSeconds so LLM can correlate with transcript
          retentionCurve: retentionPoints ? retentionPoints.map(p => ({
            percent: p.percent,
            absoluteRetention: p.absoluteRetention,
            relativeRetention: p.relativeRetention,
            timestampSeconds: p.timestampSeconds
          })) : []
        };

        contextContent.push({
          type: 'youtubeStats',
          name: 'YouTube Analytics Data (All-Time)',
          content: JSON.stringify(formattedYouTubeData, null, 2)
        });
        console.log('[CHAT] YouTube stats included in context:', formattedYouTubeData);
      }

      // Add session comments if available and enabled
      if (sessionComments.length > 0 && includeCommentsInContext) {
        const formattedComments = sessionComments.map((c: any) => ({
          timestamp: c.timestamp_seconds,
          formattedTime: c.timestamp_seconds ?
            `${Math.floor(c.timestamp_seconds / 3600).toString().padStart(2, '0')}:${Math.floor((c.timestamp_seconds % 3600) / 60).toString().padStart(2, '0')}:${Math.floor(c.timestamp_seconds % 60).toString().padStart(2, '0')}` :
            'no timestamp',
          content: c.content,
          author: c.user_name || 'Anonymous',
          type: c.type || 'comment',
          createdAt: c.created_at
        }));
        contextContent.push({
          type: 'comments',
          name: 'Session Comments & Annotations',
          content: JSON.stringify(formattedComments, null, 2)
        });
        console.log('[CHAT] Comments included in context:', formattedComments.length);
      }

      // Convert to string for API (backward compatible with existing ttmlContent parameter)
      let combinedContextContent = contextContent.length > 0
        ? JSON.stringify(contextContent)
        : ttmlContent; // Fallback to original ttmlContent if no combined content

      console.log('[CHAT] Context content array:', contextContent.map(c => ({ type: c.type, name: c.name })));
      console.log('[CHAT] combinedContextContent first 500 chars:', combinedContextContent?.substring(0, 500));
      console.log('[CHAT] Combined context content being sent:', combinedContextContent ? `${contextContent.length} items` : 'None');

      // Add session context if any (only enabled sessions) - load full data
      const enabledSessions = sessionContext.filter(session => session.includedInContext);

      // Load full session data for enabled sessions (transcript and TTML)
      const enabledSessionsWithData = await Promise.all(
        enabledSessions.map(async (session) => {
          if (!session.transcript || !session.ttmlFiles) {
            console.log(`[CHAT] Loading full data for session ${session.sessionId}`);
            try {
              const fullSessionData = await sessionService.getSessionForContext(session.sessionId, token || undefined);
              return {
                ...session,
                transcript: fullSessionData.transcript,
                ttmlFiles: fullSessionData.ttmlFiles
              };
            } catch (error) {
              console.error(`[CHAT] Failed to load session data for ${session.sessionId}:`, error);
              return session; // Return original if loading fails
            }
          }
          return session; // Already has data
        })
      );
      
      // Build context for enabled sessions
      let sessionContextContent = '';
      if (enabledSessionsWithData.length > 0) {
        const sessionContextPrompt = `
**SESSION CONTEXT:**

The following are transcripts and TTML files from other sessions that are relevant to this conversation. Use this context to provide more comprehensive analysis and insights:

${enabledSessionsWithData.map(session => {
  const speakerInfo = session.speakerCount > 0 ? ` (${session.speakerCount} speakers: ${session.speakerNames.join(', ')})` : '';
  const sessionType = session.isYouTubeMode ? '🎥 YouTube Video' : '🎙️ Audio File';
  
  let content = `### ${sessionType}: ${session.title}${speakerInfo}
**Session ID:** ${session.sessionId}
**Filename:** ${session.filename}
**Word Count:** ${session.wordCount} words

**TRANSCRIPT:**
\`\`\`
${session.transcript ? processTranscriptForContext(session.transcript, session.wordCount) : '[Transcript will be loaded when needed]'}
\`\`\``;

  if (session.ttmlFiles && session.ttmlFiles.length > 0) {
    content += `\n\n**TTML FILES:**\n${session.ttmlFiles.map(file => `\n#### ${file.name}\n\`\`\`xml\n${file.content}\n\`\`\``).join('\n')}`;
  }

  return content;
}).join('\n\n---\n\n')}`;
        sessionContextContent = sessionContextPrompt;
      }

      // Build file context if any files are uploaded
      let fileContextContent = '';
      const enabledFiles = uploadedFiles.filter(file => fileContextToggles[file.id] !== false);
      if (enabledFiles.length > 0) {
        fileContextContent = `\n\n**UPLOADED FILES CONTEXT:**\n\n${enabledFiles.map(file => {
          return `### File: ${file.name}\n**Type:** ${file.type}\n**Size:** ${file.size} bytes\n\n\`\`\`\n${file.content || '[Content not loaded]'}\n\`\`\``;
        }).join('\n\n---\n\n')}`;
      }

      // Prepare the full context
      let fullContext = transcript;
      if (sessionContextContent) {
        fullContext += '\n\n' + sessionContextContent;
      }
      if (fileContextContent) {
        fullContext += '\n\n' + fileContextContent;
      }
      if (useStorytellingContext) {
        fullContext += '\n\n' + STORYTELLING_CONTEXT;
      }
      
      // Execute the chat request
      await openRouterService.chatWithTranscriptStreaming(
        fullContext,
        model,
        chatMessages,
        (chunk) => {
          if (abortControllerRef.current?.signal.aborted) {
            return;
          }
          streamingContentRef.current += chunk;
          setStreamingContent(streamingContentRef.current);
        },
        (ttml: string, filename: string) => {
          const newTtmlFile: TTMLFile = {
            id: filename,
            content: ttml,
            messageIndex: messagesToSend.length,
            createdAt: new Date(),
            filename
          };
          setTtmlFiles(prev => [...prev, newTtmlFile]);
          setTtmlNotification(`TTML file "${filename}" ready for download!`);
          setTimeout(() => setTtmlNotification(''), 5000);
        },
        () => {
          // onComplete - handled after the try/catch
        },
        (error: string) => {
          console.error('[CHAT] Streaming error:', error);
          setError(error);
        },
        token || undefined,
        combinedContextContent || ttmlContent,
        enabledFiles,
        sessionId,
        abortController,
        false, // TTML output disabled
        undefined, // onToolCall
        undefined, // onToolError
        activeConversationId,
        isPublicView ? BYOKService.getApiKey() : null, // BYOK: Pass user's API key for public shares
        reasoningStrength !== 'none' ? { effort: reasoningStrength } : null, // OpenRouter extended thinking
        textOnlyTimeReferences, // Disable timecode:// links in responses when true
        isPublicView ? shareToken : null
      );

      // FIRST: Clear streaming state to prevent duplicate message display
      setIsStreaming(false);
      setStreamingContent('');
      const completeResponse = streamingContentRef.current;
      streamingContentRef.current = '';
      abortControllerRef.current = null;

      // THEN: Add the complete response as an assistant message
      const updatedMessages: ChatMessage[] = [...messagesToSend, { role: 'assistant', content: completeResponse }];
      setMessages(updatedMessages);

      // Check for auto-naming after adding assistant message
      checkAndAutoNameConversation(updatedMessages);

      // Save conversation state - pass updatedMessages to avoid stale closure
      await saveCurrentConversationState(true, updatedMessages);

    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      console.error('[CHAT] Error in sendMessageFromIndex:', err);
      setError(errorMessage);
      // Clear streaming state on error
      setIsStreaming(false);
      setStreamingContent('');
      streamingContentRef.current = '';
      abortControllerRef.current = null;
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || isStreaming || !transcript) return;
    
    // Check share permissions
    if (sharePermissions && sharePermissions.chatMode === 'DISABLED') {
      setError('Chat is disabled for this share link');
      return;
    }
    
    if (sharePermissions && sharePermissions.chatMode === 'READ_ONLY') {
      setError('Chat is read-only for this share link');
      return;
    }

    // Check for API key on public shares (BYOK)
    if (isPublicView && !BYOKService.hasApiKey()) {
      setError('Please enter your OpenRouter API key to use chat. Click the settings button above to add your key.');
      return;
    }

    console.log('[CHAT] sendMessage called, sessionContext state:', sessionContext);

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setError('');
    setStreamingContent('');
    
    // Reset retry state when user sends a new manual message (not automated retry)
    // This prevents false positives when users send new requests
    if (!isAutomatedRetryInProgress) {
      setRetryCount(0);
      setLastFailedTool(null);
      setLastFailedMessage(null);
      console.log('[CHAT] Reset retry state for new user message');
    } else {
      console.log('[CHAT] Automated retry in progress, preserving retry state');
      setIsAutomatedRetryInProgress(false); // Reset the flag after use
    }

    // Add user message
    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsStreaming(true);
    streamingContentRef.current = '';
    
    // Check for auto-naming after adding user message
    checkAndAutoNameConversation(newMessages);

    // Create AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Convert existing messages to the format expected by the streaming API
      const chatMessages = newMessages.map(msg => ({ role: msg.role, content: msg.content }));

      // Get all current TTML content if available and enabled
      console.log('[CHAT] Current TTML files:', currentTtmlFiles);
      console.log('[CHAT] Include TTML in context:', includeTTMLInContext);
      let ttmlContent = (currentTtmlFiles.length > 0 && includeTTMLInContext)
        ? JSON.stringify(currentTtmlFiles.map(file => ({ name: file.name, content: file.content }))) 
        : undefined;
      console.log('[CHAT] TTML content being sent:', ttmlContent ? 'Present' : 'Not present');
      
      // Get narrative/topic map content if available and enabled
      console.log('[CHAT-REGEN] Narrative data available:', !!narrativeData);
      console.log('[CHAT-REGEN] Include topic map in context:', includeTopicMapInContext);

      // Combine TTML and topic map content into a single context object
      let contextContent: any[] = [];
      if (currentTtmlFiles.length > 0 && includeTTMLInContext) {
        contextContent.push(...currentTtmlFiles.map(file => ({
          type: 'ttml',
          name: file.name,
          content: file.content
        })));
      }
      if (narrativeData && includeTopicMapInContext) {
        console.log('[CHAT-REGEN] Adding narrative/topic map to context, data keys:', Object.keys(narrativeData));
        contextContent.push({
          type: 'topicMap',
          name: 'Narrative Structure / Topic Map',
          content: JSON.stringify(narrativeData, null, 2)
        });
      } else if (includeTopicMapInContext && !narrativeData) {
        console.warn('[CHAT-REGEN] Topic map toggle is ON but narrativeData is not available yet');
      }

      // Add YouTube retention data and stats if available and enabled
      if (youtubeRetentionData && includeYouTubeStatsInContext) {
        const ytStats = youtubeRetentionData.videoStats;
        const retentionPoints = youtubeRetentionData.retentionData;

        // Format retention data for context
        const formattedYouTubeData = {
          videoId: youtubeRetentionData.videoId,
          videoDurationSeconds: youtubeRetentionData.videoDurationSeconds,
          fetchedAt: youtubeRetentionData.fetchedAt,
          stats: ytStats ? {
            views: ytStats.views,
            likes: ytStats.likes,
            comments: ytStats.comments,
            shares: ytStats.shares,
            subscribersGained: ytStats.subscribersGained,
            subscribersLost: ytStats.subscribersLost,
            estimatedMinutesWatched: ytStats.estimatedMinutesWatched,
            averageViewDuration: ytStats.averageViewDuration,
            averageViewPercentage: ytStats.averageViewPercentage,
            videoTitle: ytStats.videoTitle,
            videoPublishedAt: ytStats.videoPublishedAt
          } : null,
          // Include all retention data points for fine-grained analysis
          // Each point has timestampSeconds so LLM can correlate with transcript
          retentionCurve: retentionPoints ? retentionPoints.map(p => ({
            percent: p.percent,
            absoluteRetention: p.absoluteRetention,
            relativeRetention: p.relativeRetention,
            timestampSeconds: p.timestampSeconds
          })) : []
        };

        contextContent.push({
          type: 'youtubeStats',
          name: 'YouTube Analytics Data (All-Time)',
          content: JSON.stringify(formattedYouTubeData, null, 2)
        });
        console.log('[CHAT-REGEN] YouTube stats included in context:', formattedYouTubeData);
      }

      // Add session comments if available and enabled
      if (sessionComments.length > 0 && includeCommentsInContext) {
        const formattedComments = sessionComments.map((c: any) => ({
          timestamp: c.timestamp_seconds,
          formattedTime: c.timestamp_seconds ?
            `${Math.floor(c.timestamp_seconds / 3600).toString().padStart(2, '0')}:${Math.floor((c.timestamp_seconds % 3600) / 60).toString().padStart(2, '0')}:${Math.floor(c.timestamp_seconds % 60).toString().padStart(2, '0')}` :
            'no timestamp',
          content: c.content,
          author: c.user_name || 'Anonymous',
          type: c.type || 'comment',
          createdAt: c.created_at
        }));
        contextContent.push({
          type: 'comments',
          name: 'Session Comments & Annotations',
          content: JSON.stringify(formattedComments, null, 2)
        });
        console.log('[CHAT-REGEN] Comments included in context:', formattedComments.length);
      }

      // Convert to string for API (backward compatible with existing ttmlContent parameter)
      let combinedContextContent = contextContent.length > 0
        ? JSON.stringify(contextContent)
        : ttmlContent; // Fallback to original ttmlContent if no combined content

      console.log('[CHAT-REGEN] Context content array:', contextContent.map(c => ({ type: c.type, name: c.name })));
      console.log('[CHAT-REGEN] combinedContextContent first 500 chars:', combinedContextContent?.substring(0, 500));
      console.log('[CHAT-REGEN] Combined context content being sent:', combinedContextContent ? `${contextContent.length} items` : 'None');

      // Add session context if any (only enabled sessions) - load full data
      const enabledSessions = sessionContext.filter(session => session.includedInContext);
      console.log('[CHAT] Session context data:', sessionContext);
      console.log('[CHAT] Enabled sessions:', enabledSessions);
      
      // Load full session data for enabled sessions (transcript and TTML)
      const enabledSessionsWithData = await Promise.all(
        enabledSessions.map(async (session) => {
          if (!session.transcript || !session.ttmlFiles) {
            console.log(`[CHAT] Loading full data for session ${session.sessionId}`);
            try {
              const fullSessionData = await sessionService.getSessionForContext(session.sessionId, token || undefined);
              return {
                ...session,
                transcript: fullSessionData.transcript,
                ttmlFiles: fullSessionData.ttmlFiles
              };
            } catch (error) {
              console.error(`[CHAT] Failed to load session data for ${session.sessionId}:`, error);
              return session; // Return original if loading fails
            }
          }
          return session; // Already has data
        })
      );
      
      console.log('[CHAT] Enabled sessions with loaded data:', enabledSessionsWithData);
      console.log('[CHAT] Session context detailed data:');
      enabledSessionsWithData.forEach(session => {
        console.log(`[CHAT] Session ${session.sessionId}:`, {
          title: session.title,
          hasTranscript: !!session.transcript,
          transcriptLength: session.transcript?.length || 0,
          hasTtml: !!session.ttmlFiles,
          ttmlCount: session.ttmlFiles?.length || 0
        });
      });
      
      if (enabledSessionsWithData.length > 0) {
        const sessionContextPrompt = `
**SESSION CONTEXT:**

The following are transcripts and TTML files from other sessions that are relevant to this conversation. Use this context to provide more comprehensive analysis and insights:

${enabledSessionsWithData.map(session => {
  const speakerInfo = session.speakerCount > 0 ? ` (${session.speakerCount} speakers: ${session.speakerNames.join(', ')})` : '';
  const sessionType = session.isYouTubeMode ? '🎥 YouTube Video' : '🎙️ Audio File';
  
  let content = `### ${sessionType}: ${session.title}${speakerInfo}
**Session ID:** ${session.sessionId}
**Filename:** ${session.filename}
**Word Count:** ${session.wordCount} words

**TRANSCRIPT:**
\`\`\`
${session.transcript ? processTranscriptForContext(session.transcript, session.wordCount) : '[Transcript will be loaded when needed]'}
\`\`\``;

  if (session.ttmlFiles && session.ttmlFiles.length > 0) {
    content += `

**TTML FILES:**
${session.ttmlFiles.map(ttml => `
**${ttml.name}:**
\`\`\`ttml
${ttml.content}
\`\`\`
`).join('')}`;
  }

  return content;
}).join('\n\n---\n\n')}

When referencing information from these sessions in your response, always specify which session you're referencing (e.g., "In the session '${enabledSessionsWithData[0]?.title || 'Session Title'}' (${enabledSessionsWithData[0]?.sessionId?.substring(0, 8) || 'ID'}...)").
`;
        
        combinedContextContent = combinedContextContent
          ? `${sessionContextPrompt}\n\n${combinedContextContent}`
          : sessionContextPrompt;

        console.log('[CHAT] Session context prompt added to combinedContextContent:', sessionContextPrompt.substring(0, 200) + '...');
        console.log('[CHAT] Full session context prompt length:', sessionContextPrompt.length);
        console.log('[CHAT] Final combinedContextContent length after adding session context:', combinedContextContent?.length || 0);
      }

      // Add uploaded files context if any (only enabled files)
      const enabledFiles = uploadedFiles.filter(file => fileContextToggles[file.id] !== false);
      if (enabledFiles.length > 0) {
        const filesContext = `
**UPLOADED FILES CONTEXT:**

${enabledFiles.map(file => {
  if (file.type === 'text') {
    return `**${file.name} (Text File):**
\`\`\`
${file.content}
\`\`\``;
  } else if (file.type === 'pdf') {
    return `**${file.name} (PDF File):**
[PDF content included as base64 - ${formatFileSize(file.size)}]`;
  } else if (file.type === 'image') {
    return `**${file.name} (Image File):**
[Image included as base64 - ${formatFileSize(file.size)}]`;
  }
  return '';
}).join('\n\n')}

Please analyze these files in conjunction with the transcript content.
`;
        
        combinedContextContent = combinedContextContent
          ? `${filesContext}\n\nCONTEXT DATA:\n${combinedContextContent}`
          : filesContext;
      }

      // Add storytelling context if enabled
      if (useStorytellingContext) {
        const storytellingPrompt = `
**STORYTELLING CONTEXT FOR VIDEO EDITING ANALYSIS:**

The following is comprehensive storytelling knowledge that will help you provide better analysis and recommendations for video editing. This knowledge covers narrative techniques that are crucial for engagement and should inform your analysis:

\`\`\`
${STORYTELLING_CONTEXT}
\`\`\`

When analyzing content or providing editing recommendations, please apply these storytelling principles to help identify:
- Opportunities for stronger narrative structure
- Ways to enhance emotional engagement
- Techniques to improve pacing and tension
- Methods to create more compelling hooks and reveals
- Approaches to better connect with the audience

Use this storytelling framework to enhance your analysis and provide more valuable, engagement-focused recommendations.
`;

        combinedContextContent = combinedContextContent
          ? `${storytellingPrompt}\n\n${combinedContextContent}`
          : storytellingPrompt;
      }
      
      // Send only enabled files without content - server will load it to reduce payload size
      const uploadedFilesWithContent = enabledFiles;

      // Build session context string for the system prompt
      let sessionContextString = '';
      if (enabledSessionsWithData.length > 0) {
        sessionContextString = `
**SESSION CONTEXT:**

The following are transcripts and TTML files from other sessions that are relevant to this conversation. Use this context to provide more comprehensive analysis and insights:

${enabledSessionsWithData.map(session => {
  const speakerInfo = session.speakerCount > 0 ? ` (${session.speakerCount} speakers: ${session.speakerNames.join(', ')})` : '';
  const sessionType = session.isYouTubeMode ? '🎥 YouTube Video' : '🎙️ Audio File';

  let content = `### ${sessionType}: ${session.title}${speakerInfo}
**Session ID:** ${session.sessionId}
**Filename:** ${session.filename}
**Word Count:** ${session.wordCount} words

**TRANSCRIPT:**
\`\`\`
${session.transcript ? processTranscriptForContext(session.transcript, session.wordCount) : '[Transcript will be loaded when needed]'}
\`\`\``;

  if (session.ttmlFiles && session.ttmlFiles.length > 0) {
    content += `

**TTML FILES:**
${session.ttmlFiles.map((ttml: any) => `
**${ttml.name}:**
\`\`\`ttml
${ttml.content}
\`\`\`
`).join('')}`;
  }

  return content;
}).join('\n\n---\n\n')}
`;
      }

      // Build file context string
      let fileContextString = '';
      if (enabledFiles.length > 0) {
        fileContextString = `
**UPLOADED FILES CONTEXT:**

${enabledFiles.map(file => {
  if (file.type === 'text') {
    return `**${file.name} (Text File):**
\`\`\`
${file.content}
\`\`\``;
  } else if (file.type === 'pdf') {
    return `**${file.name} (PDF File):**
[PDF content included as base64 - ${formatFileSize(file.size)}]`;
  } else if (file.type === 'image') {
    return `**${file.name} (Image File):**
[Image included as base64 - ${formatFileSize(file.size)}]`;
  }
  return '';
}).join('\n\n')}
`;
      }

      // Use async service for non-public views, streaming for public views (BYOK)
      const useAsyncService = !isPublicView && sessionId && activeConversationId && token;

      if (useAsyncService) {
        // Build the complete system prompt
        const systemPrompt = buildAsyncSystemPrompt(
          transcript,
          contextContent,
          sessionContextString,
          fileContextString,
          useStorytellingContext,
          textOnlyTimeReferences
        );

        // Convert messages to the format needed (excluding the just-added user message since we pass it as prompt)
        const previousMessages = messages.map(msg => ({ role: msg.role, content: msg.content }));

        console.log('[ASYNC-CHAT] Submitting job with system prompt length:', systemPrompt.length);
        console.log('[ASYNC-CHAT] Previous messages count:', previousMessages.length);

        // Get enabled files to pass for multimodal processing
        const enabledFilesForAsync = uploadedFiles.filter(file => fileContextToggles[file.id] !== false);
        console.log('[ASYNC-CHAT] Enabled files for multimodal:', enabledFilesForAsync.map(f => ({ id: f.id, name: f.name, type: f.type, mimeType: f.mimeType })));

        // Submit to async service
        const jobResult = await SessionService.createAsyncChatJob(
          sessionId,
          activeConversationId,
          userMessage,
          enableWebSearch ? `${model}:online` : model,
          {
            disableTimecodeLinks: textOnlyTimeReferences,
            autoName: true,
          },
          token,
          systemPrompt,
          previousMessages,
          enabledFilesForAsync.map(f => ({
            id: f.id,
            name: f.name,
            type: f.type,
            size: f.size,
            mimeType: f.mimeType
          }))
        );

        console.log('[ASYNC-CHAT] Job created:', jobResult.jobId);

        // Start polling for this job (per-conversation tracking)
        setActiveJobs(prev => {
          const updated = new Map(prev);
          updated.set(activeConversationId, {
            jobId: jobResult.jobId,
            prompt: userMessage,
            status: 'pending',
          });
          return updated;
        });

        // Note: The polling useEffect will handle completion and update messages
        // We keep isStreaming true to show the loading state
        return;
      }

      // Fallback to streaming for public views (BYOK)
      await openRouterService.chatWithTranscriptStreaming(
        transcript,
        enableWebSearch ? `${model}:online` : model,
        chatMessages,
        // onChunk callback
        (content: string, totalTokens: number) => {
          console.log('[CHAT] onChunk called with content:', content?.length || 0, 'chars, total:', streamingContentRef.current.length);
          streamingContentRef.current += content;
          
          setStreamingContent(streamingContentRef.current);
        },
        // onTtmlBlock callback
        (ttml: string, filename: string) => {
            const newTtmlFile: TTMLFile = {
                id: filename,
                content: ttml,
                messageIndex: messages.length, // Associate with upcoming message
                createdAt: new Date()
            };
            setTtmlFiles(prev => [...prev, newTtmlFile]);
            setTtmlNotification(`TTML file "${filename}" ready for download!`);
            setTimeout(() => setTtmlNotification(''), 5000);
        },
        // onComplete callback
        (usage?: any) => {
          let finalContent = streamingContentRef.current;

          console.log('[CHAT] Final content preview:', finalContent.substring(0, 200));
          console.log('[CHAT] Current messages length:', messages.length);

          // FIRST: Clear streaming state to prevent duplicate message display
          // This must happen before adding the message to avoid a render where both are shown
          setIsStreaming(false);
          setStreamingContent('');
          streamingContentRef.current = '';
          abortControllerRef.current = null;

          // Extract TTML blocks from the completed message
          const newTtmlFiles = extractTTMLBlocks(finalContent, messages.length);
          console.log('[CHAT] Extracted TTML files:', newTtmlFiles);

          if (newTtmlFiles.length > 0) {
            setTtmlFiles(prev => {
              const updated = [...prev, ...newTtmlFiles];
              console.log('[CHAT] Updated TTML files array:', updated);
              return updated;
            });
            setTtmlNotification(`${newTtmlFiles.length} TTML file${newTtmlFiles.length > 1 ? 's' : ''} ready for download!`);

            // Clear notification after 5 seconds
            setTimeout(() => setTtmlNotification(''), 5000);
          }

          // THEN: Add the final message to the messages array
          setMessages(prev => {
            // Check if message was already added to prevent duplicates
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content === finalContent) {
              console.log('[CHAT] Skipping duplicate message');
              return prev;
            }

            const updated = [...prev, { role: 'assistant' as const, content: finalContent }];
            console.log('[CHAT] Updated messages array length:', updated.length);

            // Save conversation and check for auto-naming - pass updated messages to avoid stale closure
            setTimeout(async () => {
              await saveCurrentConversationState(true, updated);
              await checkAndAutoNameConversation(updated);
            }, 100);

            return updated;
          });
          console.log('Streaming completed with usage:', usage);
        },
        // onError callback
        (error: string) => {
          console.error('Streaming error:', error);
          // Only show error if it's not a user cancellation
          if (!error.includes('cancelled') && !error.includes('aborted')) {
            setError(error);
            
            // Implement intelligent self-healing for provider errors
            if (error.includes('Provider returned error') || error.includes('tool_call') || error.includes('streaming')) {
              console.log('[CHAT] Attempting self-healing for provider error...');
              
              // Check if this is a repeated provider error
              const currentUserMessage = newMessages[newMessages.length - 1]?.content || '';
              const isRepeatedProviderError = lastFailedMessage === currentUserMessage && error.includes('Provider returned error');
              
              if (isRepeatedProviderError && retryCount >= maxRetries) {
                console.error(`[CHAT] Provider error retry limit exceeded. Manual intervention required.`);
                setError(`Provider connection failed after ${maxRetries} attempts. Please try again later or check your connection.`);
                setRetryCount(0);
                setLastFailedMessage(null);
                return;
              }
              
              // Update retry tracking for provider errors
              if (!isRepeatedProviderError) {
                setRetryCount(1);
                setLastFailedMessage(currentUserMessage);
              } else {
                setRetryCount(prev => prev + 1);
              }
              
              // Create more specific recovery prompts based on error type
              let recoveryPrompt = '';
              const attemptNumber = isRepeatedProviderError ? retryCount + 1 : 1;
              
              if (error.includes('tool_call') || error.includes('during continuation')) {
                recoveryPrompt = `I encountered a tool execution error during processing. Let me try a simpler approach to ${currentUserMessage}.

**Attempt ${attemptNumber}/${maxRetries}** - Using alternative method without complex tool calls.`;
              } else if (error.includes('streaming') || error.includes('continuation stream')) {
                recoveryPrompt = `I encountered a streaming error during processing. Let me retry your request: "${currentUserMessage}"

**Attempt ${attemptNumber}/${maxRetries}** - Retrying with improved error handling.`;
              } else if (error.includes('recursive')) {
                recoveryPrompt = `I encountered an error during follow-up processing. Let me restart your request: "${currentUserMessage}"

**Attempt ${attemptNumber}/${maxRetries}** - Using simplified processing approach.`;
              } else {
                recoveryPrompt = `I encountered a provider error. Let me try a different approach to: "${currentUserMessage}"

**Attempt ${attemptNumber}/${maxRetries}** - Using alternative processing method.`;
              }
              
              // Implement progressive delay for provider retries
              const retryDelay = 2000 * Math.pow(1.5, attemptNumber - 1);
              
              // Add recovery message and retry with backoff
              setTimeout(() => {
                // Set the automated retry flag for provider errors too
                setIsAutomatedRetryInProgress(true);
                
                setMessages(prev => [...prev, { 
                  role: 'user' as const, 
                  content: recoveryPrompt 
                }]);
                setError(''); // Clear the error
                setStreamingContent(`\n\n*Retrying with enhanced error handling (attempt ${attemptNumber}/${maxRetries})...*`);
                
                // Auto-trigger retry after showing the recovery message
                setTimeout(() => {
                  if (!isStreaming) {
                    const syntheticEvent = {
                      key: 'Enter',
                      shiftKey: false,
                      preventDefault: () => {},
                      currentTarget: { value: '' }
                    };
                    handleKeyPress(syntheticEvent as any);
                  }
                }, 1000);
              }, retryDelay);
              
              return;
            }
          }
          setMessages(newMessages); // Keep user message, remove any partial response
          setStreamingContent('');
          streamingContentRef.current = '';
          setIsStreaming(false);
          abortControllerRef.current = null; // Clear the abort controller
        },
        token!,
        combinedContextContent,
        uploadedFilesWithContent,
        sessionId,
        abortController,
        false, // TTML output disabled
        (toolName: string, toolResult: string) => {
          // Tool call handling
        },
        (toolName: string, error: string) => {
          // Tool error handling
        },
        activeConversationId,
        isPublicView ? BYOKService.getApiKey() : null, // BYOK: Pass user's API key for public shares
        reasoningStrength !== 'none' ? { effort: reasoningStrength } : null, // OpenRouter extended thinking
        textOnlyTimeReferences, // Disable timecode:// links in responses when true
        isPublicView ? shareToken : null
      );

    } catch (err) {
      console.error('Chat failed:', err);
      // Only show error if it's not a user cancellation
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      if (!errorMessage.includes('cancelled') && !errorMessage.includes('aborted')) {
        setError(errorMessage);
      }
      setMessages(newMessages); // Keep user message
      setStreamingContent('');
      setIsStreaming(false);
      abortControllerRef.current = null; // Clear the abort controller
    }
  };

  const clearConversation = () => {
    // Stop any ongoing streaming
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setMessages([]);
    setError('');
    setStreamingContent('');
    setTtmlFiles([]);
    setTtmlNotification('');
    setTtmlAddedToSystem(false);

    // Clear files completely when clearing conversation
    setUploadedFiles([]);
    setFileContextToggles({});

    // Clear session context
    setSessionContext([]);
    
    streamingContentRef.current = '';
    if (isStreaming) {
      setIsStreaming(false);
    }
    
    // Update session to reflect cleared files and session context
    if (sessionId && token) {
      sessionService.updateSession({
        sessionId,
        uploadedFiles: [],
        sessionContext: []
      }, token).catch(error => {
        console.error('Failed to update session with cleared context:', error);
      });
    }
  };

  // Auto-add TTML to system message when available (idempotent)
  useEffect(() => {
    if (currentTtmlFiles.length > 0 && !ttmlAddedToSystem) {
      setTtmlAddedToSystem(true);
      setTtmlNotification(`${currentTtmlFiles.length} TTML file${currentTtmlFiles.length > 1 ? 's' : ''} automatically added to chat context for video editing assistance`);
      setTimeout(() => setTtmlNotification(''), 3000);
    }
  }, [currentTtmlFiles, ttmlAddedToSystem]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatModelName = (modelString: string) => {
    return modelString.split('/').map(part => 
      part.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    ).join(' / ');
  };

  // Function to detect narrative/story keywords and auto-enable storytelling context

  // Copy message content to clipboard
  const copyToClipboard = async (content: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    }
  };

  // Pin message to comments
  const pinMessageToComments = async (messageContent: string, modelName: string) => {
    if (!sessionId) {
      setError('No session available to pin message');
      return;
    }

    try {
      const success = await addPinnedMessage(messageContent, modelName);
      if (success) {
        setError(''); // Clear any existing errors
        // Could add a success notification here
      } else {
        setError('Failed to pin message to comments');
      }
    } catch (err) {
      console.error('Error pinning message:', err);
      setError('Failed to pin message to comments');
    }
  };

  // File handling functions
  const handleFileUpload = async (files: FileList) => {
    for (const file of Array.from(files)) {
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        setError(`File ${file.name} is too large. Maximum size is 50MB.`);
        continue;
      }

      // If we have a sessionId, upload to persistent storage
      if (sessionId) {
        try {
          const uploadResult = await sessionService.uploadFile(sessionId, file, token || undefined);
          
          const newFile = {
            id: uploadResult.file.id,
            name: uploadResult.file.name,
            type: uploadResult.file.type,
            size: uploadResult.file.size,
            mimeType: uploadResult.file.mimeType,
            uploadedAt: uploadResult.file.uploadedAt,
            // Don't load content immediately for persistent files
            content: undefined,
            s3Key: undefined, // This will be set by the backend
            isLoading: false,
            includedInContext: true // Default to enabled
          };

          setUploadedFiles(prev => [...prev, newFile]);
          setFileContextToggles(prev => ({ ...prev, [newFile.id]: true }));
        } catch (error) {
          console.error('Failed to upload file to session:', error);
          setError(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } else {
        // Fallback to in-memory storage for sessions without persistence
        const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        let fileType: 'text' | 'pdf' | 'image';
        let content: string;

        if (file.type.startsWith('image/')) {
          fileType = 'image';
          content = await fileToBase64(file);
        } else if (file.type === 'application/pdf') {
          fileType = 'pdf';
          content = await fileToBase64(file);
        } else if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
          fileType = 'text';
          content = await file.text();
        } else {
          setError(`Unsupported file type: ${file.type}`);
          continue;
        }

        const newFile = {
          id: fileId,
          name: file.name,
          type: fileType,
          content,
          size: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
          isLoading: false,
          includedInContext: true // Default to enabled
        };

        setUploadedFiles(prev => [...prev, newFile]);
        setFileContextToggles(prev => ({ ...prev, [fileId]: true }));
      }
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // Remove data:type;base64, prefix
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const imageFiles: File[] = [];
    
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }
    
    if (imageFiles.length > 0) {
      const fileList = new DataTransfer();
      imageFiles.forEach(file => fileList.items.add(file));
      await handleFileUpload(fileList.files);
    }
  };

  const removeFile = async (fileId: string) => {
    // If we have a sessionId, delete from persistent storage
    if (sessionId) {
      try {
        await sessionService.deleteFile(sessionId, fileId, token || undefined);
      } catch (error) {
        console.error('Failed to delete file from session:', error);
        setError(`Failed to delete file: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return; // Don't remove from local state if server deletion failed
      }
    }
    
    // Remove from local state
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setFileContextToggles(prev => {
      const newToggles = { ...prev };
      delete newToggles[fileId];
      return newToggles;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Toggle file inclusion in context
  const toggleFileInContext = async (fileId: string) => {
    const newToggleState = !fileContextToggles[fileId];
    
    setFileContextToggles(prev => ({
      ...prev,
      [fileId]: newToggleState
    }));
    
    // Update the uploadedFiles array to maintain consistency
    const updatedFiles = uploadedFiles.map(file => 
      file.id === fileId 
        ? { ...file, includedInContext: newToggleState }
        : file
    );
    
    setUploadedFiles(updatedFiles);
    
    // Persist to session if we have a sessionId
    if (sessionId && token) {
      try {
        await sessionService.updateSession({
          sessionId,
          uploadedFiles: updatedFiles
        }, token);
      } catch (error) {
        console.error('Failed to update file context toggle in session:', error);
        // Revert local state if the server update failed
        setFileContextToggles(prev => ({
          ...prev,
          [fileId]: !newToggleState
        }));
        setUploadedFiles(uploadedFiles); // Revert to original state
        setError('Failed to update file context setting');
      }
    }
  };

  // Handle session context updates
  const handleSessionContextUpdate = async (updatedSessionContext: SessionContextItem[]) => {
    setSessionContext(updatedSessionContext);
    
    // Persist to session if we have a sessionId
    if (sessionId && token) {
      try {
        await sessionService.updateSession({
          sessionId,
          sessionContext: updatedSessionContext
        }, token);
      } catch (error) {
        console.error('Failed to update session context:', error);
        setError('Failed to update session context');
      }
    }
  };

  // Check if we're in a tab layout (no fullscreen needed)
  const isInTabLayout = !isFullscreen && typeof window !== 'undefined' && 
    window.location.pathname.includes('/session/');

  const containerClasses = isFullscreen 
    ? "fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col"
    : isInTabLayout
      ? "h-full flex flex-col bg-white dark:bg-gray-800"
      : "bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700";

  const contentClasses = isFullscreen
    ? "flex-1 flex flex-col p-4 space-y-4 overflow-hidden"
    : isInTabLayout
      ? "flex-1 flex flex-col p-4 space-y-4 overflow-hidden"
      : "p-6 space-y-6";

  const messagesContainerClasses = isFullscreen
    ? "flex-1 overflow-y-auto space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg min-h-0"
    : isInTabLayout
      ? "flex-1 overflow-y-auto space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg min-h-0"
      : "h-96 overflow-y-auto space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg overflow-x-hidden";

  // Memoize markdown components to prevent unnecessary re-renders
  const markdownComponents = useMemo(() => ({
    code: ({ className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      const isInline = !props.node || props.node.tagName !== 'pre';
      const isCodeBlock = !isInline && match;

      if (isCodeBlock && match[1] === 'ttml') {
        // Don't render TTML blocks inline - they'll be handled as downloads
        return null;
      }

      return (
        <code
          className={`${className} ${isInline
            ? 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 px-1 py-0.5 rounded text-xs'
            : 'block bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 p-2 rounded text-xs overflow-x-auto'}`}
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }: any) => <pre className="bg-gray-200 dark:bg-gray-600 rounded p-2 overflow-x-auto my-2">{children}</pre>,
    p: ({ children }: any) => <div className="mb-2 last:mb-0 text-gray-900 dark:text-gray-100">{children}</div>,
    ul: ({ children }: any) => <ul className="list-disc list-inside mb-2 text-gray-900 dark:text-gray-100">{children}</ul>,
    ol: ({ children }: any) => <ol className="list-decimal list-inside mb-2 text-gray-900 dark:text-gray-100">{children}</ol>,
    li: ({ children }: any) => <li className="mb-1 text-gray-900 dark:text-gray-100">{children}</li>,
    h1: ({ children }: any) => <h1 className="text-lg font-bold mb-2 text-gray-900 dark:text-gray-100">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-base font-bold mb-2 text-gray-900 dark:text-gray-100">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-sm font-bold mb-1 text-gray-900 dark:text-gray-100">{children}</h3>,
    strong: ({ children }: any) => <strong className="font-semibold text-gray-900 dark:text-gray-100">{children}</strong>,
    em: ({ children }: any) => <em className="italic text-gray-900 dark:text-gray-100">{children}</em>,
    blockquote: ({ children }: any) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300">{children}</blockquote>,
    // Table components with dark mode support
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-2">
        <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => <thead className="bg-gray-100 dark:bg-gray-700">{children}</thead>,
    tbody: ({ children }: any) => <tbody className="divide-y divide-gray-200 dark:divide-gray-600">{children}</tbody>,
    tr: ({ children }: any) => <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">{children}</tr>,
    th: ({ children }: any) => <th className="px-3 py-2 text-left font-semibold text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">{children}</th>,
    td: ({ children }: any) => <td className="px-3 py-2 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">{children}</td>,
    a: createTimeCodeComponent,
  }), [createTimeCodeComponent]);

  return (
    <ErrorBoundary>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={containerClasses}
        role={isFullscreen ? "dialog" : undefined}
        aria-modal={isFullscreen ? "true" : undefined}
        aria-labelledby={isFullscreen ? "chat-modal-title" : undefined}
        aria-describedby={isFullscreen ? "chat-modal-description" : undefined}
        tabIndex={isFullscreen ? -1 : undefined}
      >
      <div className={isInTabLayout ? "flex h-full min-h-0" : "flex h-full"}>
        {/* Conversation Sidebar */}
        <ConversationSidebar
          conversations={conversations}
          activeConversationId={activeConversationId}
          isCollapsed={isFullscreen || isInTabLayout ? isSidebarCollapsed : true}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onSelectConversation={handleSelectConversation}
          onCreateConversation={handleCreateConversation}
          onDeleteConversation={handleDeleteConversation}
          onRenameConversation={handleRenameConversation}
          onGenerateConversationName={handleGenerateConversationName}
          onDuplicateConversation={handleDuplicateConversation}
          onTogglePinConversation={handleTogglePinConversation}
          onBatchGenerate={() => setIsBatchGenerateModalOpen(true)}
          isGeneratingName={isGeneratingConversationName}
          isReadOnly={sharePermissions ? sharePermissions.chatMode === 'READ_ONLY' : false}
          activeJobs={activeJobs}
        />
        
        {/* Main Chat Area */}
        <div className={contentClasses + (isInTabLayout ? " min-h-0" : "")}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-2xl border border-[#FFB52B]/25 bg-[#FFB52B]/[0.14] p-2 shadow-[0_18px_30px_-24px_rgba(255,181,43,0.92)]">
              <MessageCircle size={24} className="text-[#232E3F] dark:text-[#FFCF74]" />
            </div>
            <div>
              <h3 
                id="chat-modal-title"
                className="text-xl font-semibold text-gray-900 dark:text-gray-100"
              >
                Chat with Transcript {isFullscreen && '(Fullscreen)'}
              </h3>
              <p 
                id="chat-modal-description"
                className="text-gray-600 dark:text-gray-400 text-sm"
              >
                Ask questions or discuss the transcript content
                <span className="inline-flex items-center gap-1 ml-2">
                  {currentTtmlFiles.length > 0 && (
                    <button
                      onClick={() => {
                        const newValue = !includeTTMLInContext;
                        setIncludeTTMLInContext(newValue);
                        // Persist preference
                        if (sessionId && token) {
                          sessionService.updateSession({
                            sessionId,
                            chatSettings: {
                              selectedModel: model,
                              useStorytellingContext,
                              enableWebSearch,
                              textOnlyTimeReferences,
                              includeTTMLInContext: newValue,
                              includeTopicMapInContext,
                              includeCommentsInContext,
                              includeYouTubeStatsInContext
                            }
                          }, token).catch(error => {
                            console.error('Failed to save TTML context preference:', error);
                          });
                        }
                      }}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                        includeTTMLInContext 
                          ? brandGoldChipClass
                          : brandMutedChipClass
                      }`}
                    >
                      {currentTtmlFiles.length} TTML File{currentTtmlFiles.length > 1 ? 's' : ''} in Context
                    </button>
                  )}
                  {narrativeData && (
                    <button
                      onClick={() => {
                        const newValue = !includeTopicMapInContext;
                        setIncludeTopicMapInContext(newValue);
                        // Persist preference
                        if (sessionId && token) {
                          sessionService.updateSession({
                            sessionId,
                            chatSettings: {
                              selectedModel: model,
                              useStorytellingContext,
                              enableWebSearch,
                              textOnlyTimeReferences,
                              includeTTMLInContext,
                              includeTopicMapInContext: newValue,
                              includeCommentsInContext,
                              includeYouTubeStatsInContext
                            }
                          }, token).catch(error => {
                            console.error('Failed to save topic map context preference:', error);
                          });
                        }
                      }}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                        includeTopicMapInContext
                          ? brandNavyChipClass
                          : brandMutedChipClass
                      }`}
                    >
                      Topic Map in Context
                    </button>
                  )}
                  {youtubeRetentionData && youtubeRetentionData.videoStats && (
                    <button
                      onClick={() => {
                        const newValue = !includeYouTubeStatsInContext;
                        setIncludeYouTubeStatsInContext(newValue);
                        // Persist preference
                        if (sessionId && token) {
                          sessionService.updateSession({
                            sessionId,
                            chatSettings: {
                              selectedModel: model,
                              useStorytellingContext,
                              enableWebSearch,
                              textOnlyTimeReferences,
                              includeTTMLInContext,
                              includeTopicMapInContext,
                              includeYouTubeStatsInContext: newValue
                            }
                          }, token).catch(error => {
                            console.error('Failed to save YouTube stats context preference:', error);
                          });
                        }
                      }}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                        includeYouTubeStatsInContext
                          ? brandGoldChipClass
                          : brandMutedChipClass
                      }`}
                    >
                      YouTube Stats in Context
                    </button>
                  )}
                  {sessionComments.length > 0 && (
                    <button
                      onClick={() => {
                        const newValue = !includeCommentsInContext;
                        setIncludeCommentsInContext(newValue);
                        // Persist preference
                        if (sessionId && token) {
                          sessionService.updateSession({
                            sessionId,
                            chatSettings: {
                              selectedModel: model,
                              useStorytellingContext,
                              enableWebSearch,
                              textOnlyTimeReferences,
                              includeTTMLInContext,
                              includeTopicMapInContext,
                              includeCommentsInContext: newValue,
                              includeYouTubeStatsInContext
                            }
                          }, token).catch(error => {
                            console.error('Failed to save comments context preference:', error);
                          });
                        }
                      }}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                        includeCommentsInContext
                          ? brandNavyChipClass
                          : brandMutedChipClass
                      }`}
                      title={`${sessionComments.length} comments ${includeCommentsInContext ? 'included in' : 'excluded from'} context`}
                    >
                      Comments in Context ({sessionComments.length})
                    </button>
                  )}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
            
            {isFullscreen && (
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Close fullscreen"
              >
                <X size={18} />
              </button>
            )}

            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Clear conversation"
              >
                <RotateCcw size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Chat Settings */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
          <div
            onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
            className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsSettingsExpanded(!isSettingsExpanded); }}
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Chat Settings</span>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <span>{formatModelName(model).split(' ')[0]}</span>
                {useStorytellingContext && <span>• Story</span>}
                {enableWebSearch && <span>• Web</span>}
                {uploadedFiles.length > 0 && (
                  <span>• {uploadedFiles.filter(f => fileContextToggles[f.id] !== false).length}/{uploadedFiles.length} files</span>
                )}
                {sessionContext.length > 0 && (
                  <span>• {sessionContext.filter(s => s.includedInContext).length}/{sessionContext.length} sessions</span>
                )}
              </div>
              {/* Toggle for text-only time references */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newValue = !textOnlyTimeReferences;
                  setTextOnlyTimeReferences(newValue);

                  // Persist to session
                  if (sessionId && token) {
                    sessionService.updateSession({
                      sessionId,
                      chatSettings: {
                        selectedModel: model,
                        useStorytellingContext,
                        enableWebSearch,
                        textOnlyTimeReferences: newValue,
                        includeTTMLInContext,
                        includeTopicMapInContext
                      }
                    }, token).catch(error => {
                      console.error('Failed to save text-only preference:', error);
                    });
                  }
                }}
                className={`
                  px-2 py-1 text-xs rounded-md transition-colors
                  ${textOnlyTimeReferences
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                  }
                `}
                title={textOnlyTimeReferences ? "Switch to widget view" : "Switch to text-only view"}
              >
                {textOnlyTimeReferences ? 'Text' : 'Widget'}
              </button>
              {isSettingsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
          
          <AnimatePresence>
            {isSettingsExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-3 pt-0 space-y-4">
                  {/* Two-column layout for model selection + files vs toggles */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Left Column: Model Selection + File Upload */}
                    <div className="space-y-4">
                      {/* Model Selection */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Chat Model
                        </label>
                        <select
                          value={model}
                          onChange={(e) => {
                            const newModel = e.target.value;
                            onModelChange(newModel);
                            // Persist model selection to session
                            if (sessionId && token) {
                              sessionService.updateSession({
                                sessionId,
                                chatSettings: {
                                  selectedModel: newModel,
                                  useStorytellingContext,
                                  enableWebSearch,
                                  textOnlyTimeReferences,
                                  includeTTMLInContext,
                                  includeTopicMapInContext
                                }
                              }, token).catch(error => {
                                console.error('Failed to save model selection:', error);
                              });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={isLoading}
                        >
                          {supportedModels.map((modelOption) => (
                            <option key={modelOption} value={modelOption}>
                              {formatModelName(modelOption)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* BYOK API Key Section (for public shares) */}
                      {isPublicView && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              <Key className="inline w-4 h-4 mr-1" />
                              OpenRouter API Key
                            </label>
                            {hasValidApiKey && (
                              <span className="text-xs text-[#B57700] dark:text-[#FFCF74]">Connected</span>
                            )}
                          </div>

                          {hasValidApiKey ? (
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg text-sm font-mono">
                                {BYOKService.getMaskedKey()}
                              </div>
                              <button
                                onClick={() => {
                                  BYOKService.clearApiKey();
                                  setUserApiKey('');
                                  setShowApiKeyInput(true);
                                }}
                                className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm transition-colors"
                              >
                                Clear
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="relative">
                                <input
                                  type={showApiKey ? 'text' : 'password'}
                                  value={userApiKey}
                                  onChange={(e) => {
                                    setUserApiKey(e.target.value);
                                    setApiKeyError('');
                                  }}
                                  placeholder="sk-or-v1-..."
                                  className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowApiKey(!showApiKey)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                              {apiKeyError && (
                                <p className="text-xs text-red-600 dark:text-red-400">{apiKeyError}</p>
                              )}
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    if (!userApiKey.trim()) {
                                      setApiKeyError('Please enter your API key');
                                      return;
                                    }
                                    if (!BYOKService.isValidKeyFormat(userApiKey)) {
                                      setApiKeyError('Invalid key format. OpenRouter keys start with sk-or-');
                                      return;
                                    }
                                    BYOKService.setApiKey(userApiKey);
                                    setApiKeyError('');
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${brandPrimaryButtonClass}`}
                                >
                                  Save Key
                                </button>
                                <a
                                  href="https://openrouter.ai/keys"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`text-xs ${brandLinkClass}`}
                                >
                                  Get API Key
                                </a>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Your key is stored locally in your browser and never saved on our servers.
                              </p>
                            </div>
                          )}

                          {/* Privacy notice for public share users */}
                          <div className={`mt-3 rounded-lg p-3 ${brandNoticePanelClass}`}>
                            <p className="text-xs">
                              <strong>🔒 Your chats are private.</strong> Conversations you create here are only visible to you and won't be seen by other visitors or the session owner.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* File Upload Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Additional Context Files
                          </label>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className={`rounded px-3 py-1 text-xs transition-colors ${brandSecondaryButtonClass}`}
                          >
                            Upload Files
                          </button>
                        </div>
                        
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept=".txt,.md,.pdf,image/*"
                          onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                          className="hidden"
                        />
                        
                        <div
                          className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                            isDragOver 
                              ? 'border-[#FFB52B] bg-[#FFF8EA] dark:bg-[#263245]' 
                              : 'border-[#D6DCE4] bg-white dark:border-[#41506A] dark:bg-[#202A3A]'
                          }`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Drag & drop files or click "Upload Files"
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Supports: Text, PDFs, Images
                          </p>
                        </div>
                        
                        {uploadedFiles.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Files ({uploadedFiles.length}):
                              </p>
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {uploadedFiles.filter(f => fileContextToggles[f.id] !== false).length} in context
                              </span>
                            </div>
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {uploadedFiles.map((file) => (
                                <div key={file.id} className={`flex items-center justify-between rounded p-2 border transition-all ${
                                  fileContextToggles[file.id] !== false 
                                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600' 
                                    : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 opacity-60'
                                }`}>
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                                      file.type === 'text' ? 'border border-[#FFB52B]/40 bg-[#FFF4D8] text-[#8B5B00] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1]' :
                                      file.type === 'pdf' ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200' :
                                      'border border-[#445166] bg-[#232E3F] text-[#F4F4F4]'
                                    }`}>
                                      {file.type.toUpperCase()}
                                    </span>
                                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={file.name}>
                                      {file.name}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="flex items-center space-x-1">
                                      <input
                                        type="checkbox"
                                        id={`file-toggle-${file.id}`}
                                        checked={fileContextToggles[file.id] !== false}
                                        onChange={() => toggleFileInContext(file.id)}
                                        className={`w-3 h-3 ${brandCheckboxClass}`}
                                        title="Include in chat context"
                                      />
                                      <label 
                                        htmlFor={`file-toggle-${file.id}`} 
                                        className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer"
                                        title="Include in chat context"
                                      >
                                        Context
                                      </label>
                                    </div>
                                    <button
                                      onClick={() => removeFile(file.id)}
                                      className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                      title="Remove file"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: Feature Toggles */}
                    <div className="space-y-4">
                      {/* Web Search Toggle */}
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="web-search"
                            checked={enableWebSearch}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setEnableWebSearch(newValue);
                              // Persist web search setting to session
                              if (sessionId && token) {
                                sessionService.updateSession({
                                  sessionId,
                                  chatSettings: {
                                    selectedModel: model,
                                    useStorytellingContext,
                                    enableWebSearch: newValue,
                                    textOnlyTimeReferences,
                                    includeTTMLInContext,
                                    includeTopicMapInContext
                                  }
                                }, token).catch(error => {
                                  console.error('Failed to save web search setting:', error);
                                });
                              }
                            }}
                            className={`w-4 h-4 ${brandCheckboxClass}`}
                          />
                          <label htmlFor="web-search" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                            Enable Web Search
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 ml-7">
                          When enabled, the AI can search the web for current information and external references. Uses OpenRouter's web search feature to provide up-to-date context and fact-checking.
                        </p>
                        {enableWebSearch ? (
                          <div className="ml-7 rounded px-2 py-1 text-xs border border-[#E7B14C]/45 bg-[#FFF4D8] text-[#8B5B00] dark:border-[#8E6E33] dark:bg-[#332A17] dark:text-[#FFE3A1]">
                            ✓ Web search enabled - AI can access current web information
                          </div>
                        ) : (
                          <div className="ml-7 text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded">
                            ✗ Web search disabled - AI limited to training data
                          </div>
                        )}
                      </div>

                      {/* Storytelling Context Toggle */}
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="storytelling-context"
                            checked={useStorytellingContext}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setUseStorytellingContext(newValue);
                              // Persist storytelling setting to session
                              if (sessionId && token) {
                                sessionService.updateSession({
                                  sessionId,
                                  chatSettings: {
                                    selectedModel: model,
                                    useStorytellingContext: newValue,
                                    enableWebSearch,
                                    textOnlyTimeReferences,
                                    includeTTMLInContext,
                                    includeTopicMapInContext
                                  }
                                }, token).catch(error => {
                                  console.error('Failed to save storytelling setting:', error);
                                });
                              }
                            }}
                            className={`w-4 h-4 ${brandCheckboxClass}`}
                          />
                          <label htmlFor="storytelling-context" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                            Include Storytelling Context
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 ml-7">
                          Adds comprehensive storytelling knowledge for better video editing recommendations and narrative analysis.
                        </p>
                        {useStorytellingContext && (
                          <div className="ml-7 rounded px-2 py-1 text-xs border border-[#E7B14C]/45 bg-[#FFF4D8] text-[#8B5B00] dark:border-[#8E6E33] dark:bg-[#332A17] dark:text-[#FFE3A1]">
                            ✓ Storytelling context active
                          </div>
                        )}
                      </div>

                      {/* Disable Timecode Links Toggle */}
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="disable-timecode-links"
                            checked={textOnlyTimeReferences}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setTextOnlyTimeReferences(newValue);
                              // Persist setting to session
                              if (sessionId && token) {
                                sessionService.updateSession({
                                  sessionId,
                                  chatSettings: {
                                    selectedModel: model,
                                    useStorytellingContext,
                                    enableWebSearch,
                                    textOnlyTimeReferences: newValue,
                                    includeTTMLInContext,
                                    includeTopicMapInContext
                                  }
                                }, token).catch(error => {
                                  console.error('Failed to save timecode links setting:', error);
                                });
                              }
                            }}
                            className={`w-4 h-4 ${brandCheckboxClass}`}
                          />
                          <label htmlFor="disable-timecode-links" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                            Disable Timecode Links
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 ml-7">
                          When enabled, the AI will output plain text timestamps (e.g., "at 01:23:45") instead of clickable timecode links. Useful for exporting conversations or when timecode widgets are not needed.
                        </p>
                        {textOnlyTimeReferences ? (
                          <div className="ml-7 text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded">
                            ✓ Timecode links disabled - AI outputs plain text timestamps
                          </div>
                        ) : (
                          <div className="ml-7 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
                            ✗ Timecode links enabled - AI outputs clickable timestamp widgets
                          </div>
                        )}
                      </div>

                      {/* Reasoning Strength Dropdown */}
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <label htmlFor="reasoning-strength" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Reasoning Strength
                          </label>
                          <select
                            id="reasoning-strength"
                            value={reasoningStrength}
                            onChange={(e) => setReasoningStrength(e.target.value)}
                            className="rounded-md border border-[#CDD4DD] bg-white px-2 py-1 text-sm text-[#232E3F] focus:border-[#FFB52B] focus:ring-[#FFB52B]/25 dark:border-[#445166] dark:bg-[#202A3A] dark:text-[#F4F4F4]"
                          >
                            <option value="none">None (Disabled)</option>
                            <option value="minimal">Minimal</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="xhigh">Extra High</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 ml-0">
                          Enable extended thinking/reasoning for deeper analysis. Higher levels use more tokens but produce more thorough responses.
                        </p>
                        {reasoningStrength !== 'none' && (
                          <div className="rounded px-2 py-1 text-xs border border-[#445166] bg-[#EEF1F3] text-[#3F4E62] dark:border-[#4F5C72] dark:bg-[#273244] dark:text-[#D5DCE5]">
                            ✓ Extended thinking active ({reasoningStrength})
                          </div>
                        )}
                      </div>

                    </div>
                  </div>

                  {/* Session Context Section - Disabled for public views */}
                  {sessionId && !sharePermissions && (
                    <SessionContextManager
                      sessionId={sessionId}
                      sessionContext={sessionContext}
                      onSessionContextUpdate={handleSessionContextUpdate}
                    />
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Batch Generation Progress Indicator */}
        {activeBatch && (
          <div className="mx-4 mb-2 rounded-lg border border-[#445166] bg-[#232E3F] p-3 text-[#F4F4F4]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#FFCF74]" />
                <span className="text-sm font-medium text-[#F4F4F4]">
                  Generating conversations in background
                </span>
              </div>
              <span className="text-xs text-[#FFCF74]">
                {activeBatch.completed + activeBatch.failed} / {activeBatch.totalJobs} complete
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/10">
              <div
                className="h-1.5 rounded-full bg-[#FFB52B] transition-all duration-300"
                style={{ width: `${((activeBatch.completed + activeBatch.failed) / activeBatch.totalJobs) * 100}%` }}
              />
            </div>
            {activeBatch.failed > 0 && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {activeBatch.failed} failed
              </p>
            )}
          </div>
        )}

        {/* Chat Messages */}
        <div className={isFullscreen || isInTabLayout ? "flex-1 flex flex-col space-y-4 min-h-0 overflow-auto" : "space-y-4"}>
          {messages.length === 0 && !isLoading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <MessageCircle size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p>Start a conversation about your transcript!</p>
            </div>
          ) : (
            <div className={messagesContainerClasses}>
              {/* Starter suggestions for new conversations */}
              {messages.length === 0 && activeConversationId && (
                <div className="flex flex-col items-center justify-center py-8">
                  <MessageCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start a new conversation
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md">
                    Use the agent system for richer transcript workflows. This chat stays available for direct ad hoc questions.
                  </p>
                </div>
              )}
              
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={`${message.role}-${index}-${message.content.slice(0, 20)}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`flex items-start space-x-3 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <Bot size={16} className="text-primary-600" />
                      </div>
                    )}
                    
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-primary-600 text-white ml-auto'
                          : false
                            ? 'border border-[#E7B14C]/45 bg-[#FFF8EA] text-[#6F5934] dark:border-[#7D6334] dark:bg-[#2F2A1E] dark:text-[#FFE3A1]'
                            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 dark:text-gray-100'
                      }`}
                    >
                      <div className="text-sm">
                        {message.role === 'user' ? (
                          editingMessageIndex === index ? (
                            <div className="space-y-2">
                              <textarea
                                value={editingMessageContent}
                                onChange={(e) => setEditingMessageContent(e.target.value)}
                                className="w-full p-2 text-gray-900 bg-white border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                                rows={4}
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveEdit(index)}
                                  className="px-3 py-1 text-xs text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors"
                                >
                                  Save & Execute
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1 text-xs text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap">{message.content}</div>
                          )
                        ) : (
                          <div className="prose prose-sm max-w-none dark:prose-invert">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              urlTransform={(url) => {
                                // Allow timecode:// protocol
                                if (url.startsWith('timecode://')) {
                                  return url;
                                }
                                // Default behavior for other URLs
                                return url;
                              }}
                              components={markdownComponents}
                            >
                              {(() => {
                                const content = removeCodeBlocks(message.content);
                                debugContent(content);
                                return content;
                              })()}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>

                      {/* TTML Download Buttons */}
                      {message.role === 'assistant' && ttmlFiles.filter(f => f.messageIndex === index).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="text-xs text-gray-600 mb-2">TTML Files Generated:</div>
                          <div className="flex flex-wrap gap-2">
                            {ttmlFiles
                              .filter(f => f.messageIndex === index)
                              .map((ttmlFile) => (
                                <button
                                  key={ttmlFile.id}
                                  onClick={() => downloadTTML(ttmlFile)}
                                  className="flex items-center space-x-1 rounded border border-[#FFB52B]/40 bg-[#FFF4D8] px-2 py-1 text-xs text-[#8B5B00] transition-colors hover:bg-[#FFE7AD] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1] dark:hover:bg-[#FFB52B]/[0.24]"
                                >
                                  <FileText size={12} />
                                  <span>Download {ttmlFile.filename ? ttmlFile.filename.replace('.ttml', '').toUpperCase() : ttmlFile.id.split('-')[0].toUpperCase()}</span>
                                  <Download size={10} />
                                </button>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons for All Messages */}
                      <div className={`mt-3 pt-3 border-t ${message.role === 'user' ? 'border-primary-500/30' : 'border-gray-200'} flex flex-wrap gap-2`}>
                        {/* Edit Button for User Messages */}
                        {message.role === 'user' && editingMessageIndex !== index && (
                          <button
                            onClick={() => handleEditMessage(index)}
                            className="flex items-center space-x-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition-colors"
                            title="Edit this message"
                          >
                            <Edit size={12} />
                          </button>
                        )}
                        
                        {/* Execute Button - allow for logged-in users and public share users with API key */}
                        {editingMessageIndex !== index && (!sharePermissions || sharePermissions.chatMode === 'FULL' || isPublicView) && (
                          <button
                            onClick={() => handleExecuteMessage(index)}
                            className="flex items-center space-x-1 rounded border border-[#445166] bg-[#232E3F] px-2 py-1 text-xs text-[#F4F4F4] transition-colors hover:bg-[#2F3B4E]"
                            title={message.role === 'user' ? 'Re-execute from this message' : 'Re-execute from previous user message'}
                          >
                            <RefreshCw size={12} />
                          </button>
                        )}
                        
                        {/* Delete Button - allow for logged-in users and public share users with API key */}
                        {editingMessageIndex !== index && (!sharePermissions || sharePermissions.chatMode === 'FULL' || isPublicView) && (
                          <button
                            onClick={() => {
                              if (window.confirm('Delete this message?')) {
                                handleDeleteMessage(index);
                              }
                            }}
                            className="flex items-center space-x-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded transition-colors"
                            title="Delete this message"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        
                        {/* Copy Button for Assistant Messages */}
                        {message.role === 'assistant' && message.content.length > 100 && (
                          <button
                            onClick={() => copyToClipboard(message.content, index)}
                            className={`flex items-center space-x-1 text-xs px-2 py-1 rounded transition-colors ${
                              copiedMessageIndex === index
                                ? 'border border-[#FFB52B]/40 bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1] dark:hover:bg-[#FFB52B]/[0.24]'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            title="Copy message to clipboard"
                          >
                            {copiedMessageIndex === index ? (
                              <>
                                <Check size={12} />
                              </>
                            ) : (
                              <>
                                <Copy size={12} />
                              </>
                            )}
                          </button>
                        )}

                        {/* Pin to Comments Button for Assistant Messages - only for logged-in users, not public shares */}
                        {message.role === 'assistant' && sessionId && message.content.length > 100 && !isPublicView && (!sharePermissions || sharePermissions.chatMode === 'FULL') && (
                          <button
                            onClick={() => pinMessageToComments(message.content, model)}
                            className="flex items-center space-x-1 text-xs bg-yellow-100 dark:bg-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-700 text-yellow-700 dark:text-yellow-200 px-2 py-1 rounded transition-colors"
                            title="Pin this message to comments for collaboration"
                          >
                            <Pin size={12} />
                          </button>
                        )}
                      </div>
                    </div>

                    {message.role === 'user' && (
                      <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <User size={16} className="text-gray-600" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {(isLoading || isStreaming) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start space-x-3"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <Bot size={16} className="text-primary-600" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-3 rounded-lg max-w-[70%]">
                    {isStreaming && streamingContent ? (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        <div className="prose prose-sm max-w-none dark:prose-invert">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            urlTransform={(url) => {
                              // Allow timecode:// protocol
                              if (url.startsWith('timecode://')) {
                                return url;
                              }
                              // Default behavior for other URLs
                              return url;
                            }}
                            components={markdownComponents}
                          >
                            {streamingContent}
                          </ReactMarkdown>
                          <span className="inline-block w-2 h-4 bg-primary-600 ml-1 animate-pulse"></span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                        <Loader2 className="animate-spin" size={16} />
                        <span className="text-sm">
                          {activeConversationId && activeJobs.has(activeConversationId) ? (
                            <>Processing{activeJobs.get(activeConversationId)?.status === 'running' ? '' : ' (queued)'}... You can navigate away and return later.</>
                          ) : (
                            'Thinking...'
                          )}
                        </span>
                        {activeConversationId && activeJobs.has(activeConversationId) && (
                          <button
                            onClick={handleCancelJob}
                            className="ml-2 px-2 py-1 text-xs bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-800/50 text-red-600 dark:text-red-400 rounded transition-colors flex items-center gap-1"
                            title="Cancel generation"
                          >
                            <Square size={10} className="fill-current" />
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-3"
            >
              <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* TTML Notification */}
        <AnimatePresence>
          {ttmlNotification && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="rounded-lg border border-[#E7B14C]/45 bg-[#FFF8EA] p-3 dark:border-[#79613A] dark:bg-[#2B3241]"
            >
              <div className="flex items-center space-x-2">
                <FileText size={16} className="text-[#B57700] dark:text-[#FFCF74]" />
                <p className="text-sm font-medium text-[#6F5934] dark:text-[#E4D7BD]">{ttmlNotification}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* TTML Download Area */}
        {ttmlFiles.length > 0 && (
          <div className="p-4 border-t border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
            <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Downloadable TTML Files:</h4>
            <div className="flex flex-wrap gap-2">
              {ttmlFiles.map((file) => (
                <motion.button
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => downloadTTML(file)}
                  className="flex items-center space-x-2 bg-secondary-100 dark:bg-secondary-900 text-secondary-800 dark:text-secondary-200 px-3 py-1.5 rounded-lg hover:bg-secondary-200 dark:hover:bg-secondary-800 transition-colors text-sm font-medium"
                >
                  <Download size={16} />
                  <span>{file.filename ? file.filename.replace('.ttml', '') : file.id}</span>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Message Input */}
        <div className={isInTabLayout ? "flex-shrink-0 space-y-3 mt-4" : "space-y-3"}>
          {/* Attachment Preview Area - shown when files are attached */}
          {uploadedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              {uploadedFiles.filter(f => fileContextToggles[f.id] !== false).map((file) => (
                <div
                  key={file.id}
                  className="relative group flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-2 pr-6 border border-gray-200 dark:border-gray-600 shadow-sm"
                >
                  {file.type === 'image' && file.content ? (
                    <img
                      src={`data:${file.mimeType};base64,${file.content}`}
                      alt={file.name}
                      className="h-10 w-10 object-cover rounded"
                    />
                  ) : file.type === 'image' ? (
                    <div className="flex h-10 w-10 items-center justify-center rounded border border-[#445166] bg-[#232E3F]">
                      <Image size={20} className="text-[#FFCF74]" />
                    </div>
                  ) : file.type === 'pdf' ? (
                    <div className="h-10 w-10 bg-red-100 dark:bg-red-800 rounded flex items-center justify-center">
                      <FileText size={20} className="text-red-600 dark:text-red-300" />
                    </div>
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded border border-[#FFB52B]/40 bg-[#FFF4D8] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16]">
                      <FileText size={20} className="text-[#B57700] dark:text-[#FFE3A1]" />
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 max-w-[100px] truncate" title={file.name}>
                      {file.name}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(file.id)}
                    className="absolute -top-1 -right-1 p-0.5 bg-gray-200 dark:bg-gray-600 hover:bg-red-200 dark:hover:bg-red-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove file"
                  >
                    <X size={12} className="text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              ))}
              {uploadedFiles.filter(f => fileContextToggles[f.id] === false).length > 0 && (
                <span className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  +{uploadedFiles.filter(f => fileContextToggles[f.id] === false).length} hidden
                </span>
              )}
            </div>
          )}

          {/* Context Window Usage Progress Bar */}
          {contextUsage && contextUsage.totalTokens > 0 && (
            <div className="group relative mb-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      contextUsage.status === 'green' ? 'bg-[#FFB52B]' :
                      contextUsage.status === 'yellow' ? 'bg-[#E7B14C]' :
                      contextUsage.status === 'orange' ? 'bg-[#C98A00]' :
                      'bg-[#B56A5D]'
                    }`}
                    style={{ width: `${Math.min(contextUsage.percentUsed, 100)}%` }}
                  />
                </div>
                <span className={`text-[10px] font-mono tabular-nums whitespace-nowrap ${
                  contextUsage.status === 'green' ? 'text-[#B57700] dark:text-[#FFCF74]' :
                  contextUsage.status === 'yellow' ? 'text-[#A36A00] dark:text-[#FFD67D]' :
                  contextUsage.status === 'orange' ? 'text-[#8B5B00] dark:text-[#EABF6C]' :
                  'text-[#A25D55] dark:text-[#E6B6AE]'
                }`}>
                  {contextUsage.percentUsed}%
                </span>
              </div>
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 whitespace-nowrap">
                Context: {contextUsage.totalTokens.toLocaleString()} / {contextUsage.maxTokens.toLocaleString()} tokens
                {contextUsage.compactionCount > 0 && ` (compacted ${contextUsage.compactionCount}x)`}
              </div>
            </div>
          )}

          <div
            className={`flex space-x-3 relative ${isDragOver ? 'rounded-lg ring-2 ring-[#FFB52B]' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragOver && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-[#FFF4D8]/90 dark:bg-[#232E3F]/90 pointer-events-none">
                <span className="font-medium text-[#8B5B00] dark:text-[#FFE3A1]">Drop files here</span>
              </div>
            )}
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              placeholder={sharePermissions && sharePermissions.chatMode === 'READ_ONLY' ? "Read-only access - chat is disabled" : "Ask a question about the transcript... (paste or drop files)"}
              className="flex-1 p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={2}
              disabled={isLoading || isStreaming || !transcript || (sharePermissions && sharePermissions.chatMode === 'READ_ONLY') || false}
            />
            {isStreaming ? (
              <button
                onClick={stopStreaming}
                className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                title="Stop generating response"
              >
                <Square size={16} />
                <span>Stop</span>
              </button>
            ) : (
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading || !transcript || (sharePermissions && sharePermissions.chatMode === 'READ_ONLY') || false}
                className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
              </button>
            )}
          </div>
          
          {!transcript && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Complete a transcription first to start chatting with the transcript.
            </p>
          )}
        </div>
        </div>
      </div>
    </motion.div>
    
    {/* Timeline Viewer Modal */}
    {timelineModalData && (
      <TimelineViewerModal
        isOpen={isTimelineModalOpen}
        onClose={() => {
          setIsTimelineModalOpen(false);
          setTimelineModalData(null);
        }}
        sessionId={timelineModalData.sessionId}
        startSeconds={timelineModalData.startSeconds}
        endSeconds={timelineModalData.endSeconds}
        sessionTitle={timelineModalData.sessionTitle}
      />
    )}

    {/* Batch Generate Modal */}
    <BatchGenerateModal
      isOpen={isBatchGenerateModalOpen}
      onClose={() => setIsBatchGenerateModalOpen(false)}
      selectedModel={model}
      isStarting={isBatchStarting}
      onGenerate={async (prompts: Array<{ prompt: string } | string>) => {
        if (!sessionId || !token || !transcript) return;

        setIsBatchStarting(true);
        try {
          // Build context content for batch jobs (same as regular messages)
          const contextContent: Array<{ name: string; content: string }> = [];

          // Add TTML content if enabled
          if (includeTTMLInContext && currentTtmlFiles && currentTtmlFiles.length > 0) {
            const latestTtml = currentTtmlFiles[currentTtmlFiles.length - 1];
            if (latestTtml?.content) {
              contextContent.push({
                name: 'Current TTML Captions',
                content: latestTtml.content.substring(0, 50000)
              });
            }
          }

          // Add topic map if enabled and available
          if (includeTopicMapInContext && narrativeData) {
            contextContent.push({
              name: 'Narrative Structure / Topic Map',
              content: JSON.stringify(narrativeData, null, 2).substring(0, 30000)
            });
          }

          // Add YouTube retention data and stats if available and enabled
          if (youtubeRetentionData && includeYouTubeStatsInContext) {
            const ytStats = youtubeRetentionData.videoStats;
            const retentionPoints = youtubeRetentionData.retentionData;

            const formattedYouTubeData = {
              videoId: youtubeRetentionData.videoId,
              videoDurationSeconds: youtubeRetentionData.videoDurationSeconds,
              fetchedAt: youtubeRetentionData.fetchedAt,
              stats: ytStats ? {
                views: ytStats.views,
                likes: ytStats.likes,
                comments: ytStats.comments,
                shares: ytStats.shares,
                subscribersGained: ytStats.subscribersGained,
                subscribersLost: ytStats.subscribersLost,
                estimatedMinutesWatched: ytStats.estimatedMinutesWatched,
                averageViewDuration: ytStats.averageViewDuration,
                averageViewPercentage: ytStats.averageViewPercentage,
                videoTitle: ytStats.videoTitle,
                videoPublishedAt: ytStats.videoPublishedAt
              } : null,
              retentionCurve: retentionPoints ? retentionPoints.map((p: any) => ({
                percent: p.percent,
                absoluteRetention: p.absoluteRetention,
                relativeRetention: p.relativeRetention,
                timestampSeconds: p.timestampSeconds
              })) : []
            };

            contextContent.push({
              name: 'YouTube Analytics Data (All-Time)',
              content: JSON.stringify(formattedYouTubeData, null, 2)
            });
          }

          // Add session comments if available and enabled
          if (sessionComments.length > 0 && includeCommentsInContext) {
            const formattedComments = sessionComments.map((c: any) => ({
              timestamp: c.timestamp_seconds,
              formattedTime: c.timestamp_seconds ?
                `${Math.floor(c.timestamp_seconds / 3600).toString().padStart(2, '0')}:${Math.floor((c.timestamp_seconds % 3600) / 60).toString().padStart(2, '0')}:${Math.floor(c.timestamp_seconds % 60).toString().padStart(2, '0')}` :
                'no timestamp',
              content: c.content,
              author: c.user_name || 'Anonymous',
              type: c.type || 'comment',
              createdAt: c.created_at
            }));

            contextContent.push({
              name: 'Session Comments',
              content: JSON.stringify(formattedComments, null, 2)
            });
          }

          // Build the system prompt with all context
          const batchSystemPrompt = buildAsyncSystemPrompt(
            transcript,
            contextContent,
            '', // No session context for batch
            '', // No file context for batch
            useStorytellingContext,
            true // Always disable timecode links for batch generation
          );

          console.log('[BATCH] Built system prompt with length:', batchSystemPrompt.length, 'context items:', contextContent.length);

          const result = await SessionService.createBatchChatJobs(
            sessionId,
            prompts,
            model,
            { disableTimecodeLinks: true, autoName: true },
            token,
            batchSystemPrompt
          );

          // Close modal and start background tracking
          setIsBatchGenerateModalOpen(false);
          setActiveBatch({
            batchId: result.batchId,
            totalJobs: result.totalJobs,
            completed: 0,
            failed: 0,
          });

          // Reload conversations to show the new (empty) ones
          const session = await sessionService.getSession(sessionId, token);
          if (session?.conversations) {
            setConversations(session.conversations);
          }
        } catch (error) {
          console.error('Failed to start batch generation:', error);
        } finally {
          setIsBatchStarting(false);
        }
      }}
    />
    </ErrorBoundary>
  );
};

// Custom comparison function to prevent re-renders when only audio state changes
const areEqual = (prevProps: ChatInterfaceProps, nextProps: ChatInterfaceProps) => {
  // Compare all props that actually affect rendering
  return (
    prevProps.transcript === nextProps.transcript &&
    prevProps.model === nextProps.model &&
    prevProps.supportedModels.length === nextProps.supportedModels.length &&
    prevProps.supportedModels.every((model, i) => model === nextProps.supportedModels[i]) &&
    prevProps.sessionId === nextProps.sessionId &&
    prevProps.transcriptionChecksum === nextProps.transcriptionChecksum &&
    // Compare currentTtmlFiles array
    prevProps.currentTtmlFiles?.length === nextProps.currentTtmlFiles?.length &&
    (prevProps.currentTtmlFiles || []).every((file, i) => {
      const nextFile = nextProps.currentTtmlFiles?.[i];
      return nextFile && file.name === nextFile.name && file.content === nextFile.content;
    }) &&
    // Compare speakerLabels object
    JSON.stringify(prevProps.speakerLabels) === JSON.stringify(nextProps.speakerLabels) &&
    // Compare transcriptionData - using JSON.stringify for deep comparison
    JSON.stringify(prevProps.transcriptionData) === JSON.stringify(nextProps.transcriptionData) &&
    // Compare narrativeData
    prevProps.narrativeData === nextProps.narrativeData &&
    // Compare youtubeRetentionData - important for YouTube stats context
    prevProps.youtubeRetentionData?.videoId === nextProps.youtubeRetentionData?.videoId &&
    prevProps.youtubeRetentionData?.fetchedAt === nextProps.youtubeRetentionData?.fetchedAt
  );
};

export default memo(ChatInterface, areEqual); 
